import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  CheckCircle,
  XCircle,
  Truck,
  FileText,
  FileBox,
  Receipt,
  AlertCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  ShoppingCart,
  Clock,
  Package,
  X,
  Calendar,
  RefreshCw,
  Download,
  Printer,
} from "lucide-react";
import { API_BASE_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { useRealTime } from "../context/RealTimeContext";
import CustomerOrders from "./CustomerOrders";
import PurchaseOrderForm from "../components/PurchaseOrderForm";
import CommercialInvoiceForm from "../components/CommercialInvoiceForm";
import apiService from "../services/api";
import {
  getInventoryBySKU,
  allocateInventory,
  releaseInventoryAllocation,
} from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { format } from "date-fns";
import toast from "react-hot-toast";
import html2pdf from "html2pdf.js";

// Professional Delivery Order Modal Component
const ProfessionalDeliveryOrderModal = ({
  deliveryOrder,
  order,
  isOpen,
  onClose,
  onDownload,
  companyInfo,
}) => {
  console.log("ProfessionalDeliveryOrderModal render called with:", {
    deliveryOrder,
    order,
    isOpen,
  });

  if (!deliveryOrder || !order) {
    console.log("ProfessionalDeliveryOrderModal: Missing data", {
      deliveryOrder,
      order,
    });
    return null;
  }

  if (!isOpen) {
    console.log("ProfessionalDeliveryOrderModal: Modal is not open");
    return null;
  }

  // Use companyInfo prop or fallback to default values
  const companyDetails = companyInfo || {
    name: "Axia B2B Solutions",
    address: "123 Business Park, Suite 100",
    city: "Tech City",
    state: "State",
    zipCode: "12345",
    country: "India",
    phone: "+91 98765 43210",
    email: "sales@ennovaceb2b.com",
    website: "www.ennovaceb2b.com",
    gstin: "27ABCDE1234F1Z5",
    pan: "ABCDE1234F",
    bankDetails: {
      bankName: "HDFC Bank",
      accountName: "Axia B2B Solutions",
      accountNumber: "1234567890123456",
      ifscCode: "HDFC0001234",
      branch: "Tech City Branch",
      accountType: "Current Account",
    },
  };

  const customer = order.customer || {};
  const deliveryOrderNumber =
    deliveryOrder.deliveryNumber || `DO-${deliveryOrder.id}`;
  const deliveryDate = deliveryOrder.actualDeliveryDate
    ? new Date(deliveryOrder.actualDeliveryDate)
    : new Date();
  const expectedDate = deliveryOrder.expectedDeliveryDate
    ? new Date(deliveryOrder.expectedDeliveryDate)
    : new Date();

  console.log("ProfessionalDeliveryOrderModal render:", {
    deliveryOrder,
    order,
    customer,
    poNumber: order.poNumber,
    orderNumber: order.orderNumber,
    orderId: order.id,
    deliveryOrderNumber: deliveryOrderNumber,
  });

  // Helper for customer logo/initials
  const getCustomerLogo = () => {
    if (customer?.logo) {
      return (
        <img
          src={customer.logo}
          alt={customer.businessName || customer.name}
          className="w-12 h-12 rounded-lg object-cover"
        />
      );
    }
    // Fallback: initials
    const initials = (customer?.businessName || customer?.name || "C")
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    return (
      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
        {initials}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-2 lg:p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>
        {/* Modal panel */}
        <div className="relative w-full max-w-[95vw] lg:max-w-[90vw] xl:max-w-[85vw] bg-white rounded-lg shadow-xl">
          {/* Action Buttons (Download, Close) remain outside printable area */}
          <div className="bg-gray-100 px-3 py-2 border-b flex justify-between items-center sticky top-0 z-10">
            <div className="text-xs text-gray-600">
              <strong>Delivery Order:</strong> {deliveryOrderNumber}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onDownload(deliveryOrder, order)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
              >
                Download PDF
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
              >
                Close
              </button>
            </div>
          </div>
          {/* Printable Content */}
          <div id="do-pdf-content">
            {/* Document Title */}
            <div className="do-title-bar flex justify-between items-center p-4 xl:p-6 bg-blue-50 border-b border-blue-200">
              <h1 className="text-xl font-bold text-blue-700">
                Delivery Order
              </h1>
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${
                    deliveryOrder.deliveryStatus === "pending"
                      ? "bg-yellow-400 text-yellow-900"
                      : deliveryOrder.deliveryStatus === "delivered"
                      ? "bg-green-500 text-white"
                      : deliveryOrder.deliveryStatus === "processing"
                      ? "bg-blue-500 text-white"
                      : deliveryOrder.deliveryStatus === "dispatched"
                      ? "bg-purple-500 text-white"
                      : "bg-gray-300 text-gray-800"
                  }`}
                >
                  {deliveryOrder.deliveryStatus?.toUpperCase() || "PENDING"}
                </span>
                <span className="text-xs text-gray-500">DO Number:</span>
                <span className="text-base font-mono font-bold text-blue-700">
                  {deliveryOrderNumber}
                </span>
              </div>
            </div>

            {/* From/To Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4 xl:p-6 border-b border-blue-100 bg-white">
              {/* From (Supplier) */}
              <div className="border border-gray-300 rounded-lg">
                <div className="bg-blue-50 px-3 py-1.5 border-b border-gray-300">
                  <h3 className="text-xs font-bold text-blue-700">
                    FROM (SUPPLIER)
                  </h3>
                </div>
                <div className="p-3 text-xs space-y-0.5">
                  <div className="font-bold">{companyDetails.name}</div>
                  <div>{companyDetails.address}</div>
                  <div>
                    {companyDetails.city}, {companyDetails.state}{" "}
                    {companyDetails.zipCode}
                  </div>
                  <div>{companyDetails.country}</div>
                  <div>
                    <strong>Phone:</strong> {companyDetails.phone}
                  </div>
                  <div>
                    <strong>Email:</strong> {companyDetails.email}
                  </div>
                  <div>
                    <strong>Website:</strong> {companyDetails.website}
                  </div>
                  <div>
                    <strong>GSTIN:</strong> {companyDetails.gstin}
                  </div>
                </div>
              </div>

              {/* To (Customer) */}
              <div className="border border-gray-300 rounded-lg">
                <div className="bg-blue-50 px-3 py-1.5 border-b border-gray-300">
                  <h3 className="text-xs font-bold text-blue-700">
                    SHIP TO (CUSTOMER)
                  </h3>
                </div>
                <div className="p-3 text-xs space-y-1">
                  {/* Company Information */}
                  <div className="font-bold text-blue-700 text-sm">
                    {customer.businessName || customer.name}
                  </div>

                  {/* Contact Information */}
                  {customer.name && customer.name !== customer.businessName && (
                    <div>
                      <span className="font-medium">Contact Person:</span>{" "}
                      {customer.name}
                    </div>
                  )}
                  {customer.contactName && (
                    <div>
                      <span className="font-medium">Contact:</span>{" "}
                      {customer.contactName}
                    </div>
                  )}
                  {customer.contactRole && (
                    <div>
                      <span className="font-medium">Role:</span>{" "}
                      {customer.contactRole}
                    </div>
                  )}

                  {/* Delivery Address */}
                  {(customer.shipAddressLine || customer.shipAddress) && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="font-medium text-gray-700 mb-1">
                        Delivery Address:
                      </div>
                      <div>
                        {customer.shipAddressLine || customer.shipAddress}
                      </div>
                      {(customer.shipDistrict ||
                        customer.shipCity ||
                        customer.shipState ||
                        customer.shipCountry ||
                        customer.shipPinCode ||
                        customer.shipZipCode) && (
                        <div>
                          {[
                            customer.shipDistrict || customer.shipCity,
                            customer.shipState,
                            customer.shipCountry,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                          {(customer.shipPinCode || customer.shipZipCode) &&
                            ` - ${
                              customer.shipPinCode || customer.shipZipCode
                            }`}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Billing Address (if different from delivery) */}
                  {(customer.billAddressLine || customer.billAddress) &&
                    customer.billAddressLine !== customer.shipAddressLine &&
                    customer.billAddress !== customer.shipAddress && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <div className="font-medium text-gray-700 mb-1">
                          Billing Address:
                        </div>
                        <div>
                          {customer.billAddressLine || customer.billAddress}
                        </div>
                        {(customer.billDistrict ||
                          customer.billCity ||
                          customer.billState ||
                          customer.billCountry ||
                          customer.billPinCode ||
                          customer.billZipCode) && (
                          <div>
                            {[
                              customer.billDistrict || customer.billCity,
                              customer.billState,
                              customer.billCountry,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                            {(customer.billPinCode || customer.billZipCode) &&
                              ` - ${
                                customer.billPinCode || customer.billZipCode
                              }`}
                          </div>
                        )}
                      </div>
                    )}

                  {/* Contact Details */}
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    {customer.contactMobile && (
                      <div>
                        <span className="font-medium">Mobile:</span>{" "}
                        {customer.contactMobile}
                      </div>
                    )}
                    {customer.contactAltMobile && (
                      <div>
                        <span className="font-medium">Alt Mobile:</span>{" "}
                        {customer.contactAltMobile}
                      </div>
                    )}
                    {customer.email && (
                      <div>
                        <span className="font-medium">Email:</span>{" "}
                        {customer.email}
                      </div>
                    )}
                    {customer.contactEmail &&
                      customer.contactEmail !== customer.email && (
                        <div>
                          <span className="font-medium">Contact Email:</span>{" "}
                          {customer.contactEmail}
                        </div>
                      )}
                  </div>

                  {/* Business Details */}
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    {customer.gstNumber && (
                      <div>
                        <span className="font-medium">GSTIN:</span>{" "}
                        {customer.gstNumber}
                      </div>
                    )}
                    {customer.panNumber && (
                      <div>
                        <span className="font-medium">PAN:</span>{" "}
                        {customer.panNumber}
                      </div>
                    )}
                    {customer.businessType && (
                      <div>
                        <span className="font-medium">Business:</span>{" "}
                        {customer.businessType}
                      </div>
                    )}
                    {customer.businessSize && (
                      <div>
                        <span className="font-medium">Business Size:</span>{" "}
                        {customer.businessSize}
                      </div>
                    )}
                  </div>

                  {/* GPS Location if available */}
                  {(customer.shipGpsLocation || customer.billGpsLocation) && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      {customer.shipGpsLocation && (
                        <div>
                          <span className="font-medium">Delivery GPS:</span>{" "}
                          {customer.shipGpsLocation}
                        </div>
                      )}
                      {customer.billGpsLocation && (
                        <div>
                          <span className="font-medium">Billing GPS:</span>{" "}
                          {customer.billGpsLocation}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Delivery Information */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 xl:p-6 border-b border-blue-100 bg-gray-50">
              <div className="text-center">
                <div className="text-xs font-medium text-gray-600">
                  Delivery Date
                </div>
                <div className="text-sm font-bold text-blue-700">
                  {deliveryDate.toLocaleDateString("en-IN")}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs font-medium text-gray-600">
                  Expected Date
                </div>
                <div className="text-sm font-bold text-blue-700">
                  {expectedDate.toLocaleDateString("en-IN")}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs font-medium text-gray-600">
                  Purchase Order
                </div>
                <div className="text-sm font-bold text-blue-700">
                  {order.poNumber || order.orderNumber || order.id || "N/A"}
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="p-4 xl:p-6">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-blue-50">
                    <th className="border border-gray-300 px-3 py-2 text-xs font-bold text-center">
                      Barcode/SKU
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-xs font-bold text-center">
                      Batch
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-xs font-bold text-center">
                      Qty
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-xs font-bold text-center">
                      Unit
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-xs font-bold text-center">
                      Condition
                    </th>
                    <th className="border border-gray-300 px-3 py-2 text-xs font-bold text-center">
                      Ack.
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(deliveryOrder.items || []).map((item, index) => (
                    <tr key={`item-${index}`}>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm font-mono">
                        <div className="font-medium">
                          {item.orderItem?.productSku?.name ||
                            item.orderItem?.productSku?.description ||
                            item.orderItem?.productSku?.product?.description ||
                            item.orderItem?.product?.description ||
                            item.orderItem?.productSku?.product?.name ||
                            item.orderItem?.product?.name ||
                            item.productName ||
                            "N/A"}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          SKU:{" "}
                          {item.orderItem?.productSku?.sku ||
                            item.sku ||
                            item.productId ||
                            "N/A"}
                        </div>
                        {item.orderItem?.product?.hsnCode && (
                          <div className="text-xs text-gray-400">
                            HSN: {item.orderItem.product.hsnCode}
                          </div>
                        )}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm">
                        <div className="font-medium">
                          {item.batch || item.batchNumber || "N/A"}
                        </div>
                        {item.expiryDate && (
                          <div className="text-xs text-gray-500">
                            Exp:{" "}
                            {new Date(item.expiryDate).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm font-bold text-blue-600">
                        {item.quantity}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm">
                        {item.unit || "PCS"}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm">
                        <span className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                          Good
                        </span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center">
                        <div className="w-4 h-4 border border-gray-400 mx-auto"></div>
                      </td>
                    </tr>
                  ))}
                  {/* Fill empty rows to maintain consistent height */}
                  {Array.from({
                    length: Math.max(
                      0,
                      6 - ((deliveryOrder.items || []).length || 0)
                    ),
                  }).map((_, index) => (
                    <tr key={`empty-${index}`}>
                      <td className="border border-gray-300 px-3 py-2">
                        &nbsp;
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        &nbsp;
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        &nbsp;
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        &nbsp;
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        &nbsp;
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        &nbsp;
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-50 font-bold">
                    <td
                      colSpan="2"
                      className="border border-gray-300 px-3 py-2 text-right text-sm"
                    >
                      TOTAL ITEMS DELIVERED:
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center text-sm text-blue-600">
                      {(deliveryOrder.items || []).reduce(
                        (sum, item) => sum + (item.quantity || 0),
                        0
                      ) || 0}
                    </td>
                    <td
                      colSpan="3"
                      className="border border-gray-300 px-3 py-2 text-center text-sm"
                    >
                      Items
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Delivery Details and Instructions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-4 xl:p-6">
              {/* Delivery Details */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-3">
                  Delivery Details:
                </h4>
                <div className="text-sm space-y-2">
                  <div>
                    <strong>Method:</strong>{" "}
                    {deliveryOrder.deliveryMethod || "Standard"}
                  </div>
                  <div>
                    <strong>Contact Person:</strong>{" "}
                    {deliveryOrder.contactPerson ||
                      customer.contactName ||
                      "N/A"}
                  </div>
                  <div>
                    <strong>Contact Phone:</strong>{" "}
                    {deliveryOrder.contactPhone ||
                      customer.contactMobile ||
                      "N/A"}
                  </div>
                  {deliveryOrder.trackingNumber && (
                    <div>
                      <strong>Tracking Number:</strong>{" "}
                      {deliveryOrder.trackingNumber}
                    </div>
                  )}
                  {deliveryOrder.courierName && (
                    <div>
                      <strong>Courier:</strong> {deliveryOrder.courierName}
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Summary */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-3">
                  Delivery Summary:
                </h4>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">Total Items:</div>
                      <div className="font-bold text-blue-600">
                        {(deliveryOrder.items || []).reduce(
                          (sum, item) => sum + (item.quantity || 0),
                          0
                        ) || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Status:</div>
                      <div className="font-bold text-blue-600">
                        {deliveryOrder.deliveryStatus?.toUpperCase()}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Expected:</div>
                      <div className="font-medium">
                        {expectedDate.toLocaleDateString("en-IN")}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Delivered:</div>
                      <div className="font-medium">
                        {deliveryDate.toLocaleDateString("en-IN")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Instructions */}
            {deliveryOrder.deliveryInstructions && (
              <div className="p-4 xl:p-6">
                <h4 className="text-sm font-bold text-gray-800 mb-2">
                  Special Delivery Instructions:
                </h4>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    {deliveryOrder.deliveryInstructions}
                  </p>
                </div>
              </div>
            )}

            {/* Terms & Conditions */}
            <div className="p-4 xl:p-6">
              <h4 className="text-sm font-bold text-gray-800 mb-2">
                Terms & Conditions:
              </h4>
              <div className="text-xs text-gray-600 space-y-1">
                <p>
                  1. Goods delivered as per the above delivery order are subject
                  to verification and acceptance by the customer.
                </p>
                <p>
                  2. Any discrepancies in quantity or quality must be reported
                  within 24 hours of delivery.
                </p>
                <p>
                  3. Customer signature/acknowledgement confirms receipt of
                  goods in good condition unless noted otherwise.
                </p>
                <p>
                  4. Risk and title of goods pass to the buyer upon delivery and
                  acknowledgement.
                </p>
                <p>
                  5. This delivery order is subject to our standard terms and
                  conditions of sale.
                </p>
                <p>
                  6. For any queries or concerns, contact us at{" "}
                  {companyDetails.phone} or {companyDetails.email}.
                </p>
                <p>7. E. & O.E. (Errors and Omissions Excepted)</p>
              </div>
            </div>

            {/* Footer Legal Text */}
            <div className="border-t border-gray-200 pt-6 text-xs text-gray-600 p-4 xl:p-6">
              <p className="mb-2">
                <strong>This is a computer-generated delivery order</strong> and
                does not require a physical signature unless specified. Customer
                acknowledgement confirms receipt of goods in the condition and
                quantity mentioned above. Thank you for your valuable business.
              </p>
              <div className="flex justify-between items-center">
                <span>GSTIN: {companyDetails.gstin}</span>
                <span>Page 1 of 1</span>
              </div>
            </div>
          </div>
          {/* Sticky Footer Actions (Download, Close) remain outside printable area */}
          <div className="bg-gray-100 px-3 py-2 border-t flex justify-between items-center sticky bottom-0 z-10">
            <div className="text-xs text-gray-600">
              <strong>Total Items:</strong>{" "}
              {(deliveryOrder.items || []).reduce(
                (sum, item) => sum + (item.quantity || 0),
                0
              ) || 0}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onDownload(deliveryOrder, order)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
              >
                Download PDF
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Orders() {
  const { isAdmin } = useAuth();
  const { triggerUpdate } = useRealTime();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [showDOModal, setShowDOModal] = useState(false);
  const [showViewDOModal, setShowViewDOModal] = useState(false);
  const [selectedDeliveryOrder, setSelectedDeliveryOrder] = useState(null);
  const [loadingDO, setLoadingDO] = useState(false);
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [actualDeliveryDate, setActualDeliveryDate] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("standard");
  const [deliveryStatus, setDeliveryStatus] = useState("pending");
  const [creatingDO, setCreatingDO] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showInvoicePrompt, setShowInvoicePrompt] = useState(false);
  const [lastCreatedDO, setLastCreatedDO] = useState(null);

  // New state for inventory data
  const [inventoryData, setInventoryData] = useState({});
  const [loadingInventory, setLoadingInventory] = useState(false);

  // Insufficient stock warning modal state
  const [showInsufficientStockModal, setShowInsufficientStockModal] =
    useState(false);
  const [insufficientStockItems, setInsufficientStockItems] = useState([]);
  const [pendingDOCreation, setPendingDOCreation] = useState(null);

  // Company information state
  const [companyInfo, setCompanyInfo] = useState({
    name: "Axia B2B Solutions",
    address: "123 Business Park, Suite 100",
    city: "Tech City",
    state: "State",
    zipCode: "12345",
    country: "India",
    phone: "+91 98765 43210",
    email: "sales@ennovaceb2b.com",
    website: "www.ennovaceb2b.com",
    gstin: "27ABCDE1234F1Z5",
    pan: "ABCDE1234F",
    bankDetails: {
      bankName: "HDFC Bank",
      accountName: "Axia B2B Solutions",
      accountNumber: "1234567890123456",
      ifscCode: "HDFC0001234",
      branch: "Tech City Branch",
      accountType: "Current Account",
    },
  });

  const [filters, setFilters] = useState({
    status: "",
    search: "",
    startDate: "",
    endDate: "",
  });

  // Filter collapse state
  const [showFilters, setShowFilters] = useState(false);

  // Pagination and Sorting State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });

  // Sorting Logic
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort orders
  const sortedOrders = useMemo(() => {
    let filteredOrders = [...orders];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredOrders = filteredOrders.filter((order) => {
        const orderId = (order.poNumber || order.orderNumber || order.id || "")
          .toString()
          .toLowerCase();
        const customerName = (
          order.customer?.businessName ||
          order.customer?.name ||
          ""
        ).toLowerCase();
        const customerEmail = (order.customer?.email || "").toLowerCase();
        const status = (order.status || "").toLowerCase();

        return (
          orderId.includes(searchLower) ||
          customerName.includes(searchLower) ||
          customerEmail.includes(searchLower) ||
          status.includes(searchLower)
        );
      });
    }

    // Apply status filter
    if (filters.status) {
      filteredOrders = filteredOrders.filter(
        (order) => order.status === filters.status
      );
    }

    // Apply date filters
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      filteredOrders = filteredOrders.filter((order) => {
        const orderDate = new Date(order.orderDate || order.createdAt);
        return orderDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day
      filteredOrders = filteredOrders.filter((order) => {
        const orderDate = new Date(order.orderDate || order.createdAt);
        return orderDate <= endDate;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filteredOrders.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties
        if (sortConfig.key === "customerName") {
          aValue = a.customer?.businessName || a.customer?.name || "";
          bValue = b.customer?.businessName || b.customer?.name || "";
        } else if (sortConfig.key === "orderId") {
          aValue = a.poNumber || a.orderNumber || a.id;
          bValue = b.poNumber || b.orderNumber || b.id;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return filteredOrders;
  }, [orders, sortConfig, filters]);

  // Calculate order statistics (based on all orders, not filtered)
  const orderStats = useMemo(() => {
    const stats = {
      total: orders.length,
      pending: orders.filter(
        (o) => o.status === "pending" || o.status === "submitted"
      ).length,
      approved: orders.filter((o) => o.status === "approved").length,
      processing: orders.filter(
        (o) => o.status === "processing" || o.status === "shipped"
      ).length,
      totalValue: orders.reduce((sum, o) => sum + (o.totalAmount || 0), 0),
    };
    return stats;
  }, [orders]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedOrders = sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.startDate && filters.endDate) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
      }

      const data = await apiService.getOrders(params);

      // Calculate delivered quantities for each order
      if (Array.isArray(data)) {
        data.forEach((order) => {
          const items = order.orderItems || [];
          items.forEach((item) => {
            let totalDelivered = 0;
            if (order.deliveryOrders) {
              order.deliveryOrders.forEach((deliveryOrder) => {
                // Only count items from delivery orders with status "delivered"
                if (
                  deliveryOrder.deliveryStatus === "delivered" &&
                  deliveryOrder.items
                ) {
                  deliveryOrder.items.forEach((deliveryItem) => {
                    // Check multiple possible ID matches
                    if (
                      deliveryItem.productId === item.productId ||
                      deliveryItem.productId === item.productSku?.product?.id ||
                      deliveryItem.orderItem?.productId === item.productId ||
                      deliveryItem.orderItem?.productSkuId === item.productSkuId
                    ) {
                      totalDelivered += deliveryItem.quantity;
                    }
                  });
                }
              });
            }
            item.deliveredQuantity = totalDelivered;
          });
        });
      }

      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to load orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const data = await apiService.getCompanyDetails();
      setCompanyInfo({
        name: data.name || "Axia B2B Solutions",
        address: data.addressLine1 || "123 Business Park, Suite 100",
        city: data.city || "Tech City",
        state: data.state || "State",
        zipCode: data.pinCode || "12345",
        country: data.country || "India",
        phone: data.phone || "+91 98765 43210",
        email: data.email || "sales@ennovaceb2b.com",
        website: data.website || "www.ennovaceb2b.com",
        gstin: data.gstNumber || "27ABCDE1234F1Z5",
        pan: data.panNumber || "ABCDE1234F",
      });
    } catch (error) {
      console.error("Failed to fetch company details:", error);
      // Keep default values if company details fetch fails
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchOrders();
      fetchCompanyDetails();
    }
  }, [isAdmin]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.search, filters.status, filters.startDate, filters.endDate]);

  // If user is not admin, show customer orders view
  if (!isAdmin) {
    return <CustomerOrders />;
  }

  const getStatusBadge = (status) => {
    const styles = {
      draft: {
        background: "#f3f4f6",
        color: "#374151",
        border: "1px solid #d1d5db",
      },
      pending: {
        background: "#fef3c7",
        color: "#92400e",
        border: "1px solid #fde68a",
      },
      submitted: {
        background: "#fef3c7",
        color: "#92400e",
        border: "1px solid #fde68a",
      },
      requested: {
        background: "#fef3c7",
        color: "#92400e",
        border: "1px solid #fde68a",
      },
      approved: {
        background: "#dcfce7",
        color: "#166534",
        border: "1px solid #bbf7d0",
      },
      rejected: {
        background: "#fee2e2",
        color: "#991b1b",
        border: "1px solid #fecaca",
      },
      processing: {
        background: "#dbeafe",
        color: "#1e40af",
        border: "1px solid #bfdbfe",
      },
      shipped: {
        background: "#e0e7ff",
        color: "#3730a3",
        border: "1px solid #c7d2fe",
      },
      delivered: {
        background: "#f0fdf4",
        color: "#15803d",
        border: "1px solid #bbf7d0",
      },
      partially_delivered: {
        background: "#fef3c7",
        color: "#92400e",
        border: "1px solid #fde68a",
      },
      invoiced: {
        background: "#f0fdf4",
        color: "#15803d",
        border: "1px solid #bbf7d0",
      },
      partially_invoiced: {
        background: "#fed7aa",
        color: "#9a3412",
        border: "1px solid #fdba74",
      },
      cancelled: {
        background: "#f3f4f6",
        color: "#374151",
        border: "1px solid #d1d5db",
      },
    };

    return (
      <span
        style={{
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "500",
          ...(styles[status] || styles.pending),
        }}
      >
        {status === "partially_delivered"
          ? "Partially Delivered"
          : status === "partially_invoiced"
          ? "Partially Invoiced"
          : status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return "Invalid Date";
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleViewOrder = async (order) => {
    // Navigate to dedicated View PO page
    navigate(`/admin/orders/view/${order.id}`);
  };

  const handlePrintPO = async (order) => {
    try {
      toast.loading("Generating PDF...");

      // Fetch full order details
      const fullOrder = await apiService.getOrder(order.id);

      // Create HTML content for PDF
      const content = `
        <div style="padding: 40px; font-family: Arial, sans-serif;">
          <!-- Header -->
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
            <h1 style="margin: 0; color: #333; font-size: 28px;">PURCHASE ORDER</h1>
            <p style="margin: 5px 0; color: #666; font-size: 14px;">${
              companyInfo.name
            }</p>
            <p style="margin: 5px 0; color: #666; font-size: 12px;">${
              companyInfo.address
            }, ${companyInfo.city}, ${companyInfo.state} ${
        companyInfo.zipCode
      }</p>
            <p style="margin: 5px 0; color: #666; font-size: 12px;">Phone: ${
              companyInfo.phone
            } | Email: ${companyInfo.email}</p>
            <p style="margin: 5px 0; color: #666; font-size: 12px;">GSTIN: ${
              companyInfo.gstin
            }</p>
          </div>

          <!-- Order Info -->
          <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
            <div>
              <h3 style="margin: 0 0 10px 0; color: #333;">Order Details</h3>
              <p style="margin: 5px 0;"><strong>PO Number:</strong> ${
                fullOrder.poNumber || fullOrder.orderNumber
              }</p>
              <p style="margin: 5px 0;"><strong>Date:</strong> ${format(
                new Date(fullOrder.orderDate || fullOrder.createdAt),
                "dd MMM yyyy"
              )}</p>
              <p style="margin: 5px 0;"><strong>Status:</strong> ${fullOrder.status.toUpperCase()}</p>
            </div>
            <div>
              <h3 style="margin: 0 0 10px 0; color: #333;">Customer Details</h3>
              <p style="margin: 5px 0;"><strong>${
                fullOrder.customer?.businessName ||
                fullOrder.customer?.name ||
                "N/A"
              }</strong></p>
              <p style="margin: 5px 0;">${
                fullOrder.customer?.addressLine1 || ""
              }</p>
              <p style="margin: 5px 0;">${fullOrder.customer?.city || ""}, ${
        fullOrder.customer?.state || ""
      } ${fullOrder.customer?.pinCode || ""}</p>
              <p style="margin: 5px 0;">Phone: ${
                fullOrder.customer?.contactMobile || "N/A"
              }</p>
              <p style="margin: 5px 0;">Email: ${
                fullOrder.customer?.email || "N/A"
              }</p>
              ${
                fullOrder.customer?.gstNumber
                  ? `<p style="margin: 5px 0;">GSTIN: ${fullOrder.customer.gstNumber}</p>`
                  : ""
              }
            </div>
          </div>

          <!-- Items Table -->
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
            <thead>
              <tr style="background-color: #f3f4f6;">
                <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">S.No</th>
                <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Product</th>
                <th style="border: 1px solid #ddd; padding: 12px; text-align: center;">Qty</th>
                <th style="border: 1px solid #ddd; padding: 12px; text-align: right;">Unit Price</th>
                <th style="border: 1px solid #ddd; padding: 12px; text-align: right;">GST %</th>
                <th style="border: 1px solid #ddd; padding: 12px; text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${fullOrder.orderItems
                ?.map(
                  (item, index) => `
                <tr>
                  <td style="border: 1px solid #ddd; padding: 12px;">${
                    index + 1
                  }</td>
                  <td style="border: 1px solid #ddd; padding: 12px;">
                    <strong>${
                      item.productSku?.name ||
                      item.productSku?.product?.name ||
                      "N/A"
                    }</strong><br/>
                    <small>SKU: ${item.productSku?.sku || "N/A"}</small>
                  </td>
                  <td style="border: 1px solid #ddd; padding: 12px; text-align: center;">${
                    item.quantity
                  }</td>
                  <td style="border: 1px solid #ddd; padding: 12px; text-align: right;">₹${(
                    item.unitPrice || 0
                  ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                  <td style="border: 1px solid #ddd; padding: 12px; text-align: right;">${
                    item.gstPercent || 0
                  }%</td>
                  <td style="border: 1px solid #ddd; padding: 12px; text-align: right;">₹${(
                    item.totalPrice || 0
                  ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>

          <!-- Totals -->
          <div style="text-align: right; margin-bottom: 30px;">
            <p style="margin: 5px 0;"><strong>Subtotal:</strong> ₹${(
              fullOrder.subtotal ||
              fullOrder.totalAmount ||
              0
            ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            <p style="margin: 5px 0;"><strong>Tax:</strong> ₹${(
              fullOrder.taxAmount || 0
            ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            <p style="margin: 10px 0 0 0; font-size: 18px;"><strong>Total Amount:</strong> ₹${(
              fullOrder.totalAmount || 0
            ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
          </div>

          <!-- Footer -->
          <div style="border-top: 1px solid #ddd; padding-top: 20px; margin-top: 40px;">
            <p style="margin: 5px 0; font-size: 12px; color: #666;">This is a computer-generated document and does not require a signature.</p>
            <p style="margin: 5px 0; font-size: 12px; color: #666;">For any queries, please contact: ${
              companyInfo.email
            } | ${companyInfo.phone}</p>
          </div>
        </div>
      `;

      // Create temporary element
      const element = document.createElement("div");
      element.innerHTML = content;
      document.body.appendChild(element);

      // Generate PDF
      const opt = {
        margin: 10,
        filename: `PO_${fullOrder.poNumber || fullOrder.orderNumber}_${format(
          new Date(),
          "yyyyMMdd"
        )}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      await html2pdf().set(opt).from(element).save();

      // Cleanup
      document.body.removeChild(element);
      toast.dismiss();
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.dismiss();
      toast.error("Failed to generate PDF");
    }
  };

  const handleApprove = (order) => {
    setSelectedOrder(order);
    setApprovalAction("approve");
    setInternalNotes("");
    setRejectionReason("");
    setShowApprovalModal(true);
  };

  const handleReject = (order) => {
    setSelectedOrder(order);
    setApprovalAction("reject");
    setInternalNotes("");
    setRejectionReason("");
    setShowApprovalModal(true);
  };

  const handleConfirmApproval = async () => {
    if (!selectedOrder) return;

    if (approvalAction === "reject" && !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    setProcessing(true);
    try {
      if (approvalAction === "approve") {
        await apiService.approveOrder(selectedOrder.id, internalNotes);
        toast.success("Order approved successfully!");
      } else {
        await apiService.rejectOrder(
          selectedOrder.id,
          rejectionReason,
          internalNotes
        );
        toast.success("Order rejected successfully!");
      }

      setShowApprovalModal(false);
      setSelectedOrder(null);
      setInternalNotes("");
      setRejectionReason("");
      await fetchOrders();

      // Trigger real-time updates for Business Intelligence and Sales Targets
      console.log("🔄 Triggering orderApproved event with data:", {
        order: selectedOrder,
        action: approvalAction,
      });
      triggerUpdate("orderApproved", {
        order: selectedOrder,
        action: approvalAction,
      });
    } catch (error) {
      console.error("Failed to process order:", error);
      toast.error(`Failed to ${approvalAction} order. Please try again.`);
    } finally {
      setProcessing(false);
    }
  };

  // Helper to determine delivery status
  function getDeliveryStatus(order) {
    const items = order.orderItems || [];

    // Calculate total delivered quantity for each item across all delivery orders
    items.forEach((item) => {
      let totalDelivered = 0;
      if (order.deliveryOrders) {
        order.deliveryOrders.forEach((deliveryOrder) => {
          // Only count items from delivery orders with status "delivered"
          if (
            deliveryOrder.deliveryStatus === "delivered" &&
            deliveryOrder.items
          ) {
            deliveryOrder.items.forEach((deliveryItem) => {
              // Check multiple possible ID matches
              if (
                deliveryItem.productId === item.productId ||
                deliveryItem.productId === item.productSku?.product?.id ||
                deliveryItem.orderItem?.productId === item.productId ||
                deliveryItem.orderItem?.productSkuId === item.productSkuId
              ) {
                totalDelivered += deliveryItem.quantity;
              }
            });
          }
        });
      }
      item.deliveredQuantity = totalDelivered;
      item.orderedQuantity = item.quantity;
    });

    const allDelivered = items.every(
      (item) =>
        (item.deliveredQuantity || 0) >= (item.orderedQuantity || item.quantity)
    );
    const anyDelivered = items.some(
      (item) => (item.deliveredQuantity || 0) > 0
    );

    if (allDelivered) return "fully";
    if (anyDelivered) return "partial";
    return "none";
  }

  // Helper to check if order has pending delivery
  function hasPendingDelivery(order) {
    return (
      order.deliveryOrders &&
      order.deliveryOrders.some((deliveryOrder) =>
        ["pending", "processing", "dispatched"].includes(
          deliveryOrder.deliveryStatus
        )
      )
    );
  }

  // Helper to check if Create DO should be disabled
  function canCreateDO(order) {
    const deliveryStatus = getDeliveryStatus(order);

    // Allow creating DO if not fully delivered (regardless of pending deliveries)
    // This enables partial deliveries - multiple DOs can be created for the same order
    if (
      order.status === "processing" ||
      order.status === "approved" ||
      order.status === "invoiced" ||
      order.status === "partially_delivered" ||
      order.status === "partially_invoiced" ||
      order.status === "delivered"
    ) {
      const canCreate = deliveryStatus !== "fully";
      return canCreate;
    }

    return false;
  }

  // Helper to check if Create Invoice should be enabled
  function canCreateInvoice(order) {
    const logPrefix = `[${order.poNumber || order.id}]`;
    console.log(`${logPrefix} Checking canCreateInvoice for order:`, {
      id: order.id,
      status: order.status,
      orderType: order.orderType,
      poNumber: order.poNumber,
      deliveryOrders: order.deliveryOrders?.length || 0,
      deliveryOrdersData: order.deliveryOrders?.map((do_) => ({
        id: do_.id,
        deliveryNumber: do_.deliveryNumber,
        invoiced: do_.invoiced,
        deliveryStatus: do_.deliveryStatus,
      })),
    });

    // Check if there are any uninvoiced AND delivered delivery orders
    const hasUninvoicedDeliveredDOs = order.deliveryOrders?.some(
      (do_) => !do_.invoiced && do_.deliveryStatus === "delivered"
    );

    console.log(
      `${logPrefix} hasUninvoicedDeliveredDOs:`,
      hasUninvoicedDeliveredDOs
    );

    if (!hasUninvoicedDeliveredDOs) {
      console.log(
        `${logPrefix} No uninvoiced delivered delivery orders available`
      );
      return false;
    }

    // For Purchase Orders (P.O.), allow invoice creation when there are delivered DOs
    if (order.orderType === "purchase" || order.poNumber) {
      console.log(
        "Purchase Order - allowing invoice creation for delivered DOs"
      );
      return true; // Already checked for delivered DOs above
    }

    // For processing orders, allow invoice creation for delivered DOs
    if (order.status === "processing") {
      console.log(
        "Processing Order - allowing invoice creation for delivered DOs"
      );
      return true; // Already checked for delivered DOs above
    }

    // For approved orders, also allow invoice creation for delivered DOs
    console.log("Approved Order - allowing invoice creation for delivered DOs");
    return true; // Already checked for delivered DOs above
  }

  // Helper to check if order already has an invoice
  function hasInvoice(order) {
    return order.invoicess && order.invoicess.length > 0;
  }

  const handleCreateDO = async (order) => {
    // Check if order is already fully delivered
    const deliveryStatus = getDeliveryStatus(order);
    if (deliveryStatus === "fully") {
      toast.error(
        "Order is already fully delivered. Cannot create additional delivery orders."
      );
      return;
    }

    // Navigate to the Create Delivery Order page
    navigate(`/admin/orders/delivery/${order.id}`);
  };

  // New function to fetch inventory data
  const fetchInventoryData = async (items) => {
    setLoadingInventory(true);
    try {
      console.log("Fetching inventory data for items:", items);

      const inventoryPromises = items.map(async (item) => {
        const productSkuId = item.productSkuId;
        const productId = item.productId;

        if (productSkuId) {
          try {
            console.log(`Fetching inventory for SKU ${productSkuId}`);
            const response = await getInventoryBySKU(productSkuId);
            console.log(
              `Inventory response for SKU ${productSkuId}:`,
              response
            );
            return { productSkuId, productId, data: response };
          } catch (error) {
            console.error(
              `Failed to fetch inventory for SKU ${productSkuId}:`,
              error
            );
            return { productSkuId, productId, data: null };
          }
        } else if (productId) {
          try {
            console.log(
              `Fetching inventory for product ${productId} (fallback)`
            );
            const response = await apiService.getInventoryByProduct(productId);
            console.log(
              `Inventory response for product ${productId}:`,
              response
            );
            return { productSkuId: null, productId, data: response };
          } catch (error) {
            console.error(
              `Failed to fetch inventory for product ${productId}:`,
              error
            );
            return { productSkuId: null, productId, data: null };
          }
        }
        return { productSkuId: null, productId: null, data: null };
      });

      const results = await Promise.all(inventoryPromises);
      const inventoryMap = {};
      results.forEach(({ productSkuId, productId, data }) => {
        if (data) {
          if (productSkuId) {
            inventoryMap[productSkuId] = data;
          } else if (productId) {
            inventoryMap[productId] = data;
          }
        }
      });

      console.log("Final inventory map:", inventoryMap);
      setInventoryData(inventoryMap);

      // Update delivery items with maxDelivery limits
      setDeliveryItems((prevItems) =>
        prevItems.map((item) => {
          const inventoryInfo =
            inventoryMap[item.productSkuId] || inventoryMap[item.productId];
          if (inventoryInfo) {
            const availableStock = inventoryInfo.availableStock || 0;
            const pendingQuantity = item.pendingQuantity || 0;
            // maxDelivery should be the minimum of available stock and pending quantity
            const maxDelivery = Math.min(availableStock, pendingQuantity);
            return { ...item, maxDelivery, availableStock };
          }
          // If no inventory data, maxDelivery is just the pending quantity
          return {
            ...item,
            maxDelivery: item.pendingQuantity || 0,
            availableStock: 0,
          };
        })
      );
    } catch (error) {
      console.error("Error fetching inventory data:", error);
      toast.error("Failed to load inventory data");
    } finally {
      setLoadingInventory(false);
    }
  };

  // Helper function to get inventory info for a product/SKU
  const getInventoryInfo = (productId, productSkuId) => {
    // Try SKU first, then fallback to product ID
    const data =
      inventoryData[productSkuId] ||
      (productId ? inventoryData[productId] : null);
    console.log(
      `Getting inventory info for product ${
        productId || "undefined"
      }, SKU ${productSkuId}:`,
      data
    );

    if (!data)
      return {
        available: "Loading...",
        total: "Loading...",
        reserved: "Loading...",
      };

    const result = {
      available: data.availableStock,
      total: data.totalStock,
      reserved: data.totalReserved,
    };

    console.log(
      `Inventory result for product ${
        productId || "undefined"
      }, SKU ${productSkuId}:`,
      result
    );
    return result;
  };

  // Helper function to get batch information for a product/SKU
  const getBatchInfo = (productId, productSkuId) => {
    // Try SKU first, then fallback to product ID
    const data =
      inventoryData[productSkuId] ||
      (productId ? inventoryData[productId] : null);

    if (!data || !data.items) return [];

    // Group items by batch and calculate available quantities
    const batchMap = {};
    data.items.forEach((item) => {
      const batchKey = item.batch || item.batchNumber || "No Batch";
      const availableQty = item.quantity - item.reservedQuantity;

      if (availableQty > 0) {
        if (!batchMap[batchKey]) {
          batchMap[batchKey] = {
            batch: batchKey,
            available: 0,
            total: 0,
            reserved: 0,
            expiryDate: item.expiryDate,
            dateReceived: item.dateReceived,
            location: item.location,
          };
        }
        batchMap[batchKey].available += availableQty;
        batchMap[batchKey].total += item.quantity;
        batchMap[batchKey].reserved += item.reservedQuantity;
      }
    });

    // Convert to array and sort by expiry date (FIFO)
    const batches = Object.values(batchMap).sort((a, b) => {
      if (a.expiryDate && b.expiryDate) {
        return new Date(a.expiryDate) - new Date(b.expiryDate);
      }
      if (a.dateReceived && b.dateReceived) {
        return new Date(a.dateReceived) - new Date(b.dateReceived);
      }
      return 0;
    });

    return batches;
  };

  // Helper function to get selected batch information (FIFO-based)
  const getSelectedBatchInfo = (productId, productSkuId) => {
    const batches = getBatchInfo(productId, productSkuId);

    if (batches.length === 0) return "No batch info";

    // Return the first batch (FIFO - oldest first)
    const selectedBatch = batches[0];
    let info = `Batch: ${selectedBatch.batch}`;

    if (selectedBatch.expiryDate) {
      info += ` | Exp: ${new Date(
        selectedBatch.expiryDate
      ).toLocaleDateString()}`;
    }

    return info;
  };

  // Helper function to select batches for delivery using FIFO logic
  const selectBatchesForDelivery = (batches, requiredQuantity) => {
    if (batches.length === 0 || requiredQuantity <= 0) return [];

    const selectedBatches = [];
    let remainingQuantity = requiredQuantity;

    // Iterate through batches in FIFO order (already sorted by expiry date)
    for (const batch of batches) {
      if (remainingQuantity <= 0) break;

      const availableFromBatch = Math.min(batch.available, remainingQuantity);

      if (availableFromBatch > 0) {
        selectedBatches.push({
          ...batch,
          allocatedQuantity: availableFromBatch,
        });

        remainingQuantity -= availableFromBatch;
      }
    }

    return selectedBatches;
  };

  // Helper function to get the correct barcode
  const getBarcode = (item) => {
    // Priority: barcode > sku > itemCode > productId
    return item.barcode || item.sku || item.itemCode || item.productId || "N/A";
  };

  const handleViewDO = async (order) => {
    console.log("handleViewDO called with order:", order);
    console.log("Order delivery orders:", order.deliveryOrders);
    console.log("Order ID:", order.id);

    if (!order.deliveryOrders || order.deliveryOrders.length === 0) {
      console.log("No delivery orders found");
      toast.error("No delivery orders found for this order");
      return;
    }

    setLoadingDO(true);
    try {
      // Get the most recent delivery order ID
      const deliveryOrderId =
        order.deliveryOrders[order.deliveryOrders.length - 1].id;
      console.log("Fetching delivery order with ID:", deliveryOrderId);
      console.log(
        "Delivery order object:",
        order.deliveryOrders[order.deliveryOrders.length - 1]
      );

      // Check if we have a valid delivery order ID
      if (!deliveryOrderId) {
        throw new Error("Invalid delivery order ID");
      }

      // Fetch both delivery order details and complete order details (with full customer data)
      console.log("Making API calls...");
      const [deliveryOrder, completeOrder] = await Promise.all([
        apiService.getDelivery(deliveryOrderId),
        apiService.getOrder(order.id),
      ]);

      console.log("Fetched delivery order:", deliveryOrder);
      console.log("Fetched complete order:", completeOrder);

      if (!deliveryOrder) {
        throw new Error("Delivery order not found");
      }

      if (!completeOrder) {
        throw new Error("Order not found");
      }

      setSelectedDeliveryOrder(deliveryOrder);
      setSelectedOrder(completeOrder);
      setShowViewDOModal(true);
      console.log("Modal should now be open");
    } catch (error) {
      console.error("Error fetching delivery order:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        orderId: order.id,
        deliveryOrders: order.deliveryOrders,
      });
      const errorMessage =
        error.message || "Failed to load delivery order details";
      toast.error(errorMessage);
    } finally {
      setLoadingDO(false);
    }
  };

  const handleCreateInvoice = (order) => {
    navigate(`/admin/orders/invoice/${order.id}`);
  };

  const handleDeliveryItemChange = (index, field, value) => {
    const updatedItems = [...deliveryItems];
    if (field === "selected") {
      updatedItems[index].selected = value;
      if (!value) {
        updatedItems[index].deliveryQuantity = 0;
        updatedItems[index].deliverNow = 0;
      } else {
        // When selected, set to maxDelivery (which is already limited by stock and pending quantity)
        updatedItems[index].deliveryQuantity = updatedItems[index].maxDelivery;
        updatedItems[index].deliverNow = updatedItems[index].maxDelivery;
      }
    } else if (field === "deliveryQuantity") {
      const newValue = parseInt(value) || 0;
      const maxAllowed = updatedItems[index].maxDelivery;
      // Ensure delivery quantity doesn't exceed maxDelivery (which is min of stock and pending)
      updatedItems[index].deliveryQuantity = Math.min(
        Math.max(0, newValue),
        maxAllowed
      );
      // Also update deliverNow to match
      updatedItems[index].deliverNow = updatedItems[index].deliveryQuantity;
    } else if (field === "deliverNow") {
      const newValue = parseInt(value) || 0;
      const maxAllowed = updatedItems[index].maxDelivery;
      // Ensure deliverNow doesn't exceed maxDelivery (which is min of stock and pending)
      updatedItems[index].deliverNow = Math.min(
        Math.max(0, newValue),
        maxAllowed
      );
      // Also update deliveryQuantity to match
      updatedItems[index].deliveryQuantity = updatedItems[index].deliverNow;
    }
    setDeliveryItems(updatedItems);
  };

  const handleAutoCreateInvoice = async (order, deliveredItems) => {
    try {
      setCreatingInvoice(true);

      // Generate invoice number - Let backend handle this
      // const invoiceNumber = `INV-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

      // Get the most recent delivery order for this order
      const latestDeliveryOrder =
        order.deliveryOrders && order.deliveryOrders.length > 0
          ? order.deliveryOrders[order.deliveryOrders.length - 1]
          : null;

      // Calculate invoice data based on delivered items
      const invoiceItems = deliveredItems.map((item) => {
        const unitPrice = item.unitPrice || 0;
        const quantity = item.deliverNow;
        const totalAmount = unitPrice * quantity;
        const gstRate = 18; // Default GST rate, you might want to get this from product
        const gstAmount = (totalAmount * gstRate) / 100;

        return {
          productSkuId: item.productSkuId,
          productName: item.name,
          quantity: quantity,
          unitPrice: unitPrice,
          totalAmount: totalAmount,
          gstRate: gstRate,
          gstAmount: gstAmount,
          cgstAmount: gstAmount / 2, // Split GST
          sgstAmount: gstAmount / 2,
          igstAmount: 0,
        };
      });

      const subtotal = invoiceItems.reduce(
        (sum, item) => sum + item.totalAmount,
        0
      );
      const totalGST = invoiceItems.reduce(
        (sum, item) => sum + item.gstAmount,
        0
      );
      const totalAmount = subtotal + totalGST;

      // Prepare invoice data for API
      const invoiceData = {
        orderId: order.id,
        // invoiceNumber: invoiceNumber, // Let backend generate this
        invoiceDate: new Date().toISOString().split("T")[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0], // 30 days from now
        paymentTerms: "Net 30",
        notes: `Invoice for delivery order items. Purchase Order: ${
          order.poNumber || order.orderNumber
        }`,
        subtotal: subtotal,
        taxAmount: totalGST,
        totalAmount: totalAmount,
        // Add delivery order information
        deliveryOrderNumber: latestDeliveryOrder?.deliveryNumber || "N/A",
        deliveryOrderDate:
          latestDeliveryOrder?.actualDeliveryDate ||
          new Date().toISOString().split("T")[0],
        items: invoiceItems.map((item) => ({
          productSkuId: item.productSkuId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          totalPrice: item.totalAmount,
          gstPercent: item.gstRate,
          gstAmount: item.gstAmount,
        })),
      };

      // Create invoice via API
      const response = await apiService.createInvoice(invoiceData);

      toast.success("Invoice created automatically for Purchase Order");

      // Generate and download invoice PDF with delivery order info
      await generateInvoicePDF(invoiceData, order, latestDeliveryOrder);
    } catch (error) {
      console.error("Error auto-creating invoice:", error);
      toast.error(
        "Failed to create invoice automatically. You can create it manually later."
      );
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleConfirmInvoice = async (invoiceData) => {
    if (!selectedOrder) return;

    setCreatingInvoice(true);
    try {
      // Get selected delivery orders
      const selectedDOs = (selectedOrder.deliveryOrders || []).filter((do_) =>
        invoiceData.deliveryOrderIds?.includes(do_.id)
      );

      // Get the most recent delivery order for this order (for backward compatibility)
      const latestDeliveryOrder =
        selectedDOs.length > 0
          ? selectedDOs[selectedDOs.length - 1]
          : selectedOrder.deliveryOrders &&
            selectedOrder.deliveryOrders.length > 0
          ? selectedOrder.deliveryOrders[
              selectedOrder.deliveryOrders.length - 1
            ]
          : null;

      // Prepare invoice data for API
      const invoiceDataForAPI = {
        orderId: selectedOrder.id,
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: invoiceData.invoiceDate,
        dueDate: invoiceData.dueDate,
        paymentTerms: invoiceData.paymentTerms,
        notes: invoiceData.notes,
        subtotal: invoiceData.subtotal,
        taxAmount:
          invoiceData.cgstAmount +
          invoiceData.sgstAmount +
          invoiceData.igstAmount,
        totalAmount: invoiceData.totalAmount,
        // Add delivery order information
        deliveryOrderIds: invoiceData.deliveryOrderIds || [],
        deliveryOrderNumber:
          invoiceData.deliveryOrderNumber ||
          latestDeliveryOrder?.deliveryNumber ||
          "N/A",
        deliveryOrderDate:
          invoiceData.deliveryOrderDate ||
          latestDeliveryOrder?.actualDeliveryDate ||
          new Date().toISOString().split("T")[0],
        items: invoiceData.items.map((item) => ({
          productSkuId: item.productSkuId || item.productId, // Send as productSkuId to match backend expectation
          quantity: item.quantity,
          unitPrice: item.rate,
          totalPrice: item.totalAmount,
          gstPercent: item.gstRate,
          gstAmount: item.cgstAmount + item.sgstAmount + item.igstAmount,
        })),
      };

      // Create invoice via API
      const response = await apiService.createInvoice(invoiceDataForAPI);

      toast.success("Commercial Invoice created successfully");

      // Close modal and refresh orders
      setShowInvoiceModal(false);
      setSelectedOrder(null);
      fetchOrders();

      // Trigger real-time updates for Business Intelligence and Sales Targets
      console.log("🔄 Triggering invoiceCreated event with data:", {
        invoice: response,
        order: selectedOrder,
      });
      triggerUpdate("invoiceCreated", {
        invoice: response,
        order: selectedOrder,
      });

      // Generate and download invoice PDF with delivery order info
      await generateInvoicePDF(
        invoiceDataForAPI,
        selectedOrder,
        latestDeliveryOrder
      );
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error("Failed to create invoice");
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleConfirmDO = async () => {
    const selectedItems = deliveryItems.filter((item) => item.deliverNow > 0);

    if (selectedItems.length === 0) {
      toast.error("Please enter quantity to deliver for at least one item");
      return;
    }

    if (!actualDeliveryDate) {
      toast.error("Please select an actual delivery date");
      return;
    }

    // Check if order is already fully delivered
    const deliveryStatus = getDeliveryStatus(selectedOrder);
    if (deliveryStatus === "fully") {
      toast.error(
        "Order is already fully delivered. Cannot create additional delivery orders."
      );
      return;
    }

    // Validate that delivery items have correct productSkuId
    const invalidItems = selectedItems.filter((item) => !item.productSkuId);
    if (invalidItems.length > 0) {
      toast.error(
        "Some items have invalid SKU information. Please refresh and try again."
      );
      return;
    }

    // Validate that quantities don't exceed pending quantities
    const overDeliveryItems = selectedItems.filter(
      (item) => item.deliverNow > item.quantity - (item.deliveredQuantity || 0)
    );
    if (overDeliveryItems.length > 0) {
      toast.error("Some items exceed the remaining quantity to be delivered.");
      return;
    }

    // Check for stock issues
    const itemsWithStockIssues = selectedItems.filter(
      (item) => item.maxDelivery < item.pendingQuantity
    );
    if (itemsWithStockIssues.length > 0) {
      // Show modal instead of window.confirm
      setInsufficientStockItems(itemsWithStockIssues);
      setPendingDOCreation({
        selectedItems,
        deliveryStatus,
        expectedDeliveryDate,
        actualDeliveryDate,
        deliveryMethod,
      });
      setShowInsufficientStockModal(true);
      return;
    }

    // Proceed with DO creation
    await createDeliveryOrder(
      selectedItems,
      deliveryStatus,
      expectedDeliveryDate,
      actualDeliveryDate,
      deliveryMethod
    );
  };

  const createDeliveryOrder = async (
    selectedItems,
    deliveryStatus,
    expectedDeliveryDate,
    actualDeliveryDate,
    deliveryMethod
  ) => {
    setCreatingDO(true);
    try {
      // Create delivery order data (without pricing for industry standard)
      const deliveryOrderData = {
        orderId: selectedOrder.id,
        // deliveryNumber: `DO-${Date.now()}-${Math.floor(Math.random() * 1000)}`, // Let backend generate this
        deliveryStatus: deliveryStatus,
        expectedDeliveryDate: new Date(expectedDeliveryDate),
        actualDeliveryDate: new Date(actualDeliveryDate),
        deliveryMethod: deliveryMethod,
        deliveryAddress: {
          businessName: selectedOrder?.customer?.businessName,
          addressLine: selectedOrder?.customer?.shipAddressLine,
          district: selectedOrder?.customer?.shipDistrict,
          state: selectedOrder?.customer?.shipState,
          pinCode: selectedOrder?.customer?.shipPinCode,
          country: selectedOrder?.customer?.shipCountry,
          gpsLocation: selectedOrder?.customer?.shipGpsLocation,
        },
        contactPerson: selectedOrder?.customer?.contactName || "N/A",
        contactPhone: selectedOrder?.customer?.contactMobile || "N/A",
        deliveryInstructions: deliveryNotes,
        items: selectedItems.map((item) => {
          // Get batch information for this item and automatically select FIFO batches
          const batchInfo = getBatchInfo(item.productId, item.productSkuId);
          const selectedBatches = selectBatchesForDelivery(
            batchInfo,
            item.deliverNow
          );

          // Use the first selected batch for display purposes (FIFO)
          const primaryBatch =
            selectedBatches.length > 0 ? selectedBatches[0] : null;

          return {
            productSkuId: item.productSkuId,
            productSkuId: item.productSkuId,
            quantity: item.deliverNow,
            productName: item.name,
            description: item.description,
            sku: item.sku || item.productId,
            unit: item.unit || "PCS",
            batch: primaryBatch?.batch || "N/A",
            batchNumber: primaryBatch?.batch || "N/A",
            barcode: getBarcode(item), // Use the proper barcode
            expiryDate: primaryBatch?.expiryDate,
            location: primaryBatch?.location,
            selectedBatches: selectedBatches, // Include all selected batches for backend processing
          };
        }),
      };

      // Debug: Log the delivery order data being sent
      console.log(
        "Sending delivery order data:",
        JSON.stringify(deliveryOrderData, null, 2)
      );
      console.log(
        "Selected items for delivery:",
        selectedItems.map((item) => ({
          productSkuId: item.productSkuId,
          productSkuId: item.productSkuId,
          name: item.name,
          sku: item.sku,
          quantity: item.quantity,
          deliveredQuantity: item.deliveredQuantity,
          deliverNow: item.deliverNow,
        }))
      );

      // Call API to create delivery order
      const createdDO = await apiService.createDeliveryOrder(deliveryOrderData);

      // Allocate inventory for the delivery order
      try {
        const allocationItems = selectedItems.map((item) => ({
          productSkuId: item.productSkuId || item.productId, // Fallback to productId if SKU not available
          quantity: item.deliverNow,
        }));

        const allocationResult = await allocateInventory(
          allocationItems,
          createdDO.id
        );

        if (allocationResult.success) {
          console.log("Inventory allocated successfully:", allocationResult);
          toast.success("Inventory allocated successfully");
        } else {
          console.warn("Some inventory allocation failed:", allocationResult);
          const failedItems = allocationResult.results.filter(
            (r) => !r.success
          );
          const failedNames = failedItems.map((f) => f.productSkuId).join(", ");
          toast.warning(
            `Delivery order created but some inventory allocation failed: ${failedNames}`
          );
        }
      } catch (allocationError) {
        console.error("Inventory allocation error:", allocationError);
        toast.warning("Delivery order created but inventory allocation failed");
      }

      toast.success("Delivery Order created successfully");

      // Generate and download PDF
      await generateProfessionalDOPDF(createdDO, selectedOrder);

      // Store the created DO for invoice prompt
      setLastCreatedDO(createdDO);

      // Close modal
      setShowDOModal(false);

      // Reset form states
      setDeliveryItems([]);
      setDeliveryNotes("");
      setExpectedDeliveryDate("");
      setActualDeliveryDate("");
      setDeliveryMethod("standard");
      setDeliveryStatus("pending");

      // For Purchase Orders, automatically create invoice
      if (selectedOrder.orderType === "purchase" || selectedOrder.poNumber) {
        console.log(
          "Purchase Order detected, automatically creating invoice..."
        );
        toast.info("Creating invoice for Purchase Order...");

        // Automatically create invoice for the delivered items
        await handleAutoCreateInvoice(selectedOrder, selectedItems);
      } else {
        // For other order types, show invoice creation prompt
        setShowInvoicePrompt(true);
      }

      // Refresh orders
      await fetchOrders();

      // Trigger real-time updates for Business Intelligence and Sales Targets
      console.log("🔄 Triggering deliveryOrderCreated event with data:", {
        deliveryOrder: createdDO,
        order: selectedOrder,
      });
      triggerUpdate("deliveryOrderCreated", {
        deliveryOrder: createdDO,
        order: selectedOrder,
      });
    } catch (error) {
      console.error("Error creating delivery order:", error);
      toast.error("Failed to create delivery order");
    } finally {
      setCreatingDO(false);
    }
  };

  const handleConfirmInsufficientStock = async () => {
    setShowInsufficientStockModal(false);
    if (pendingDOCreation) {
      const {
        selectedItems,
        deliveryStatus,
        expectedDeliveryDate,
        actualDeliveryDate,
        deliveryMethod,
      } = pendingDOCreation;
      await createDeliveryOrder(
        selectedItems,
        deliveryStatus,
        expectedDeliveryDate,
        actualDeliveryDate,
        deliveryMethod
      );
      setPendingDOCreation(null);
      setInsufficientStockItems([]);
    }
  };

  const handleCancelInsufficientStock = () => {
    setShowInsufficientStockModal(false);
    setPendingDOCreation(null);
    setInsufficientStockItems([]);
  };

  const generateInvoicePDF = async (
    invoiceData,
    order,
    deliveryOrder = null
  ) => {
    try {
      console.log("🔄 Generating invoice PDF via backend...");

      // Download PDF from backend
      const response = await fetch(
        `${API_BASE_URL}/invoices/${invoiceData.id}/download`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("❌ PDF download failed:", errorData);

        if (errorData.fallback) {
          // Handle fallback response
          const blob = new Blob([errorData.fallback], { type: "text/plain" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "fallback.txt";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.warning(
            "PDF generation failed. Downloaded fallback file instead."
          );
        } else {
          toast.error("Failed to download PDF");
        }
        return;
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `INVOICE-${
        invoiceData.invoiceNumber || invoiceData.id || "INVOICE"
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log("✅ Invoice PDF downloaded successfully");
      toast.success("Invoice PDF downloaded successfully");
    } catch (error) {
      console.error("❌ Error generating invoice PDF:", error);
      toast.error("Failed to generate invoice PDF");
    }
  };

  const generateProfessionalDOPDF = async (deliveryOrder, order) => {
    try {
      console.log("🔄 Generating delivery order PDF via backend...");

      // Get full delivery order data if not already loaded
      let fullDeliveryOrder = deliveryOrder;

      // If delivery order doesn't have items, fetch it again
      if (!fullDeliveryOrder.items || fullDeliveryOrder.items.length === 0) {
        try {
          fullDeliveryOrder = await apiService.getDelivery(deliveryOrder.id);
        } catch (error) {
          console.error("Failed to fetch delivery order:", error);
        }
      }

      // Final check - if still no items, use order items as fallback
      if (!fullDeliveryOrder.items || fullDeliveryOrder.items.length === 0) {
        fullDeliveryOrder.items = order.orderItems || [];
      }

      // Download PDF from backend
      const response = await fetch(
        `${API_BASE_URL}/delivery/${fullDeliveryOrder.id}/download`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("❌ PDF download failed:", errorData);

        if (errorData.fallback) {
          // Handle fallback response
          const blob = new Blob([errorData.fallback], { type: "text/plain" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "fallback.txt";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.warning(
            "PDF generation failed. Downloaded fallback file instead."
          );
        } else {
          toast.error("Failed to download PDF");
        }
        return;
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DO-${
        fullDeliveryOrder.deliveryNumber || fullDeliveryOrder.id || "DELIVERY"
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log("✅ Delivery order PDF downloaded successfully");
      toast.success("Delivery Order PDF downloaded successfully");
    } catch (error) {
      console.error("❌ Error generating delivery order PDF:", error);
      toast.error("Failed to generate delivery order PDF");
    }
  };

  const parseAddress = (address) => {
    if (!address)
      return {
        name: "N/A",
        address: "N/A",
        city: "N/A",
        state: "N/A",
        zipCode: "N/A",
        country: "N/A",
        phone: "N/A",
      };
    const parts = address.split(",");
    let name = parts[0].trim();
    let addressLine = parts[1].trim();
    let city = parts[2].trim();
    let state = parts[3].trim();
    let zipCode = parts[4].trim();
    let country = parts[5].trim();
    let phone = "N/A";

    // Attempt to find a phone number in the address string
    const phoneMatch = address.match(/Phone:\s*([^\s,]+)/);
    if (phoneMatch && phoneMatch[1]) {
      phone = phoneMatch[1];
    }

    return { name, address: addressLine, city, state, zipCode, country, phone };
  };

  // Smart pagination with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Orders
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage and process customer orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              // Export filtered orders to CSV
              const csvContent = [
                [
                  "Order ID",
                  "Customer",
                  "Date",
                  "Status",
                  "Amount",
                  "Items",
                  "Payment Status",
                ].join(","),
                ...sortedOrders.map((order) =>
                  [
                    order.poNumber || order.orderNumber || order.id,
                    `"${
                      order.customer?.businessName ||
                      order.customer?.name ||
                      "N/A"
                    }"`,
                    format(
                      new Date(order.orderDate || order.createdAt),
                      "dd/MM/yyyy"
                    ),
                    order.status,
                    order.totalAmount || 0,
                    order.orderItems?.length || 0,
                    (() => {
                      const totalPaid =
                        order.invoices?.reduce((sum, inv) => {
                          const invoicePaid =
                            inv.payments?.reduce(
                              (pSum, p) => pSum + (p.amount || 0),
                              0
                            ) || 0;
                          return sum + invoicePaid;
                        }, 0) || 0;
                      const percentage =
                        order.totalAmount > 0
                          ? (totalPaid / order.totalAmount) * 100
                          : 0;
                      return percentage >= 100
                        ? "Paid"
                        : percentage > 0
                        ? "Partial"
                        : "Unpaid";
                    })(),
                  ].join(",")
                ),
              ].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = `orders_${format(
                new Date(),
                "yyyy-MM-dd_HH-mm"
              )}.csv`;
              link.click();
              toast.success("Orders exported successfully");
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 shadow-lg shadow-green-500/25 transition-all duration-200"
            title="Export Orders to CSV"
          >
            <Download size={16} />
            Export
          </button>
          <button
            onClick={fetchOrders}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-500/25 transition-all duration-200"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <ShoppingCart className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {orderStats.total}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Orders
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {orderStats.pending}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Pending
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {orderStats.approved}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Approved
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
              <Package className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {orderStats.processing}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Processing
              </p>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search orders by ID, customer name..."
                    value={filters.search}
                    onChange={(e) =>
                      handleFilterChange("search", e.target.value)
                    }
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>

                {/* Filter Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-all ${
                    showFilters ||
                    filters.status ||
                    filters.startDate ||
                    filters.endDate
                      ? "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300"
                      : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {(filters.status || filters.startDate || filters.endDate) && (
                    <span className="ml-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                      {
                        [
                          filters.status,
                          filters.startDate,
                          filters.endDate,
                        ].filter(Boolean).length
                      }
                    </span>
                  )}
                </button>
              </div>

              {/* Expanded Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      Status
                    </label>
                    <select
                      value={filters.status}
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    >
                      <option value="">All Statuses</option>
                      <option value="draft">Draft</option>
                      <option value="pending">Pending</option>
                      <option value="submitted">Submitted</option>
                      <option value="requested">Requested</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      From Date
                    </label>
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) =>
                        handleFilterChange("startDate", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      To Date
                    </label>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) =>
                        handleFilterChange("endDate", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>

                  {/* Clear Filters */}
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        handleFilterChange("status", "");
                        handleFilterChange("startDate", "");
                        handleFilterChange("endDate", "");
                      }}
                      className="w-full px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                      onClick={() => handleSort("orderId")}
                    >
                      <div className="flex items-center gap-2">
                        Order ID
                        {sortConfig.key === "orderId" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp size={14} className="text-blue-600" />
                          ) : (
                            <ArrowDown size={14} className="text-blue-600" />
                          )
                        ) : (
                          <ArrowUpDown size={14} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                      onClick={() => handleSort("customerName")}
                    >
                      <div className="flex items-center gap-2">
                        Customer
                        {sortConfig.key === "customerName" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp size={14} className="text-blue-600" />
                          ) : (
                            <ArrowDown size={14} className="text-blue-600" />
                          )
                        ) : (
                          <ArrowUpDown size={14} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-2">
                        Status
                        {sortConfig.key === "status" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp size={14} className="text-blue-600" />
                          ) : (
                            <ArrowDown size={14} className="text-blue-600" />
                          )
                        ) : (
                          <ArrowUpDown size={14} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                      onClick={() => handleSort("totalAmount")}
                    >
                      <div className="flex items-center gap-2">
                        Amount
                        {sortConfig.key === "totalAmount" ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp size={14} className="text-blue-600" />
                          ) : (
                            <ArrowDown size={14} className="text-blue-600" />
                          )
                        ) : (
                          <ArrowUpDown size={14} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {paginatedOrders.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="p-4 bg-gray-100 dark:bg-slate-700 rounded-full mb-4">
                            <ShoppingCart className="w-8 h-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                            No orders found
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400">
                            Try adjusting your filters to find what you're
                            looking for.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedOrders.map((order) => (
                      <tr
                        key={order.id}
                        className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {order.poNumber ||
                                order.orderNumber ||
                                `#${order.id}`}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {formatDate(order.orderDate || order.createdAt)}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md">
                              {(
                                order.customer?.businessName ||
                                order.customer?.name ||
                                "C"
                              )
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {order.customer?.businessName ||
                                  order.customer?.name ||
                                  "N/A"}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {order.customer?.email ||
                                  order.customer?.contactMobile ||
                                  ""}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-gray-900 dark:text-white">
                                Ordered:
                              </span>
                              <span className="text-gray-600 dark:text-gray-300">
                                {order.orderItems?.reduce(
                                  (sum, item) => sum + (item.quantity || 0),
                                  0
                                ) || 0}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="font-medium text-emerald-700 dark:text-emerald-400">
                                Delivered:
                              </span>
                              <span className="text-emerald-600 dark:text-emerald-400">
                                {order.orderItems?.reduce(
                                  (sum, item) =>
                                    sum + (item.deliveredQuantity || 0),
                                  0
                                ) || 0}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(order.status)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-gray-900 dark:text-white">
                                Total:
                              </span>
                              <span className="font-semibold text-gray-900 dark:text-white">
                                ₹{order.totalAmount?.toLocaleString() || "0"}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="font-medium text-blue-700 dark:text-blue-400">
                                Paid:
                              </span>
                              <span className="text-blue-600 dark:text-blue-400">
                                ₹
                                {(() => {
                                  const totalPaid =
                                    order.invoices?.reduce((sum, inv) => {
                                      const invoicePaid =
                                        inv.payments?.reduce(
                                          (pSum, p) =>
                                            pSum + (p.amountPaid || 0),
                                          0
                                        ) || 0;
                                      return sum + invoicePaid;
                                    }, 0) || 0;
                                  return totalPaid.toLocaleString();
                                })()}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <button
                              onClick={() => handleViewOrder(order)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                              title="View Order Details"
                            >
                              <Eye size={13} />
                              <span>View</span>
                            </button>

                            <button
                              onClick={() => handlePrintPO(order)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 rounded-lg transition-colors"
                              title="Print Purchase Order"
                            >
                              <Printer size={13} />
                              <span>Print PO</span>
                            </button>

                            {(order.status === "pending" ||
                              order.status === "submitted") && (
                              <>
                                <button
                                  onClick={() => handleApprove(order)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 rounded-lg transition-colors"
                                  title="Approve Order"
                                >
                                  <CheckCircle size={13} />
                                  <span>Approve</span>
                                </button>
                                <button
                                  onClick={() => handleReject(order)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                                  title="Reject Order"
                                >
                                  <XCircle size={13} />
                                  <span>Reject</span>
                                </button>
                              </>
                            )}

                            {(order.status === "approved" ||
                              order.status === "processing" ||
                              order.status === "invoiced" ||
                              order.status === "partially_delivered" ||
                              order.status === "partially_invoiced" ||
                              order.status === "delivered") && (
                              <>
                                {canCreateDO(order) && (
                                  <button
                                    onClick={() => handleCreateDO(order)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 rounded-lg transition-colors"
                                    title="Create DO"
                                  >
                                    <Truck size={13} />
                                    <span>Create DO</span>
                                  </button>
                                )}
                                {canCreateInvoice(order) && (
                                  <button
                                    onClick={() => handleCreateInvoice(order)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 rounded-lg transition-colors"
                                    title="Create Invoice"
                                  >
                                    <FileText size={13} />
                                    <span>Create Invoice</span>
                                  </button>
                                )}
                              </>
                            )}

                            {order.invoicess && order.invoicess.length > 0 && (
                              <button
                                onClick={() => {
                                  setSelectedOrder(order);
                                  setShowInvoiceModal(true);
                                }}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 rounded-lg transition-colors"
                                title="View Invoice"
                              >
                                <Receipt size={13} />
                                <span>Inv</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Showing{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {indexOfFirstItem + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {Math.min(indexOfLastItem, sortedOrders.length)}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {sortedOrders.length}
                  </span>{" "}
                  orders
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => paginate(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-colors ${
                      currentPage === 1
                        ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  {getPageNumbers().map((page, index) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-2 text-gray-400"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => paginate(page)}
                        className={`min-w-[40px] h-10 rounded-lg font-medium transition-all ${
                          currentPage === page
                            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md"
                            : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}

                  <button
                    onClick={() =>
                      paginate(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-colors ${
                      currentPage === totalPages
                        ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        title={`${
          approvalAction.charAt(0).toUpperCase() + approvalAction.slice(1)
        } Order`}
      >
        <div style={{ marginBottom: "1rem" }}>
          <p>
            <strong>Order ID:</strong> {selectedOrder?.id}
          </p>
          <p>
            <strong>Customer:</strong>{" "}
            {selectedOrder?.customer?.businessName ||
              selectedOrder?.customer?.name}
          </p>
          <p>
            <strong>Current Status:</strong> {selectedOrder?.status}
          </p>
          <p>
            <strong>Total Amount:</strong> ₹
            {selectedOrder?.totalAmount?.toLocaleString()}
          </p>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
            }}
          >
            Internal Notes (Optional)
          </label>
          <textarea
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Add any internal notes for this decision..."
            style={{
              width: "100%",
              padding: "0.5rem",
              border: "1px solid #ddd",
              borderRadius: "0.375rem",
              minHeight: "80px",
              resize: "vertical",
            }}
          />
        </div>

        {approvalAction === "reject" && (
          <div style={{ marginBottom: "1rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "bold",
                color: "#dc2626",
              }}
            >
              Rejection Reason (Required) *
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a clear reason for rejecting this order..."
              style={{
                width: "100%",
                padding: "0.5rem",
                border: "1px solid #dc2626",
                borderRadius: "0.375rem",
                minHeight: "80px",
                resize: "vertical",
              }}
              required
            />
          </div>
        )}

        <div
          style={{
            marginTop: "1.5rem",
            display: "flex",
            justifyContent: "flex-end",
            gap: "1rem",
          }}
        >
          <Button
            onClick={() => setShowApprovalModal(false)}
            variant="secondary"
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmApproval}
            disabled={processing}
            style={{
              backgroundColor:
                approvalAction === "approve" ? "#059669" : "#dc2626",
              color: "white",
            }}
          >
            {processing
              ? "Processing..."
              : `${
                  approvalAction.charAt(0).toUpperCase() +
                  approvalAction.slice(1)
                } Order`}
          </Button>
        </div>
      </Modal>

      {/* Custom Delivery Order Modal */}
      {showDOModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl my-4 sm:my-8 flex flex-col max-h-[95vh] sm:max-h-[90vh]">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold">DELIVERY ORDER</h3>
                  <p className="text-green-100 text-sm">
                    Professional Delivery Management
                  </p>
                  {selectedOrder?.poNumber && (
                    <div className="mt-1">
                      <span className="text-xs text-green-100">
                        Purchase Order:
                      </span>
                      <span className="text-sm font-semibold ml-1">
                        {selectedOrder.poNumber}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs text-green-100">DO Number</div>
                    <div className="text-lg font-mono font-bold">{`DO-${Date.now()}-${Math.floor(
                      Math.random() * 1000
                    )}`}</div>
                  </div>
                  <button
                    onClick={() => setShowDOModal(false)}
                    className="text-green-100 hover:text-white transition-colors p-1"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleConfirmDO();
                }}
                className="p-4 sm:p-6 space-y-4 sm:space-y-6"
              >
                {/* Order Reference & Customer Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      Purchase Order Reference
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Order ID
                        </label>
                        <input
                          type="text"
                          value={`#${selectedOrder?.id || ""}`}
                          disabled
                          className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Purchase Order Number
                        </label>
                        <input
                          type="text"
                          value={
                            selectedOrder?.poNumber ||
                            selectedOrder?.orderNumber ||
                            "N/A"
                          }
                          disabled
                          className="w-full px-3 py-2 bg-blue-100 border border-blue-300 rounded-md text-sm font-semibold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Order Date
                        </label>
                        <input
                          type="text"
                          value={
                            selectedOrder?.orderDate
                              ? format(
                                  new Date(selectedOrder.orderDate),
                                  "dd/MM/yyyy"
                                )
                              : "N/A"
                          }
                          disabled
                          className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Order Value
                        </label>
                        <input
                          type="text"
                          value={`₹${
                            selectedOrder?.totalAmount?.toLocaleString() || "0"
                          }`}
                          disabled
                          className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                    </div>
                    {/* Additional Purchase Order Information */}
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Order Status
                          </label>
                          <input
                            type="text"
                            value={
                              selectedOrder?.status?.toUpperCase() || "N/A"
                            }
                            disabled
                            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Order Type
                          </label>
                          <input
                            type="text"
                            value={selectedOrder?.orderType || "Standard Order"}
                            disabled
                            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                    <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Customer Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">Business:</span>{" "}
                        {selectedOrder?.customer?.businessName || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">Contact:</span>{" "}
                        {selectedOrder?.customer?.contactName || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">Mobile:</span>{" "}
                        {selectedOrder?.customer?.contactMobile || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span>{" "}
                        {selectedOrder?.customer?.email || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">GST:</span>{" "}
                        {selectedOrder?.customer?.gstNumber || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Schedule */}
                <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                  <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                    Delivery Schedule & Status
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Expected Delivery Date
                      </label>
                      <input
                        type="date"
                        value={expectedDeliveryDate}
                        onChange={(e) =>
                          setExpectedDeliveryDate(e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Actual Delivery Date
                      </label>
                      <input
                        type="date"
                        value={actualDeliveryDate}
                        onChange={(e) => setActualDeliveryDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Delivery Method
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                        value={deliveryMethod}
                        onChange={(e) => setDeliveryMethod(e.target.value)}
                      >
                        <option value="standard">Standard Delivery</option>
                        <option value="express">Express Delivery</option>
                        <option value="overnight">Overnight Delivery</option>
                        <option value="scheduled">Scheduled Delivery</option>
                        <option value="pickup">Customer Pickup</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Delivery Status
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                        value={deliveryStatus}
                        onChange={(e) => setDeliveryStatus(e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="dispatched">Dispatched</option>
                        <option value="in_transit">In Transit</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Customer Information Block - Organized */}
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    CUSTOMER DETAILS & DELIVERY ADDRESS
                    <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                      From Customer Registration
                    </span>
                  </h4>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Business Information */}
                    <div className="bg-white p-3 rounded border-l-4 border-blue-500">
                      <div className="font-bold text-blue-700 mb-2 text-sm">
                        Business Information
                      </div>
                      <div className="text-sm space-y-1">
                        <div className="font-semibold text-gray-900">
                          {selectedOrder?.customer?.businessName ||
                            "Not provided"}
                        </div>
                        {selectedOrder?.customer?.gstNumber && (
                          <div>
                            <span className="font-medium">GSTIN:</span> $
                            {selectedOrder.customer.gstNumber}
                          </div>
                        )}
                        {selectedOrder?.customer?.panNumber && (
                          <div>
                            <span className="font-medium">PAN:</span> $
                            {selectedOrder.customer.panNumber}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Industry:</span>{" "}
                          {selectedOrder?.customer?.businessType ||
                            "Not specified"}
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="bg-white p-3 rounded border-l-4 border-green-500">
                      <div className="font-bold text-green-700 mb-2 text-sm">
                        Primary Contact
                      </div>
                      <div className="text-sm space-y-1">
                        <div>
                          <span className="font-medium">Name:</span>{" "}
                          {selectedOrder?.customer?.contactName ||
                            selectedOrder?.customer?.name ||
                            "Not provided"}
                        </div>
                        <div>
                          <span className="font-medium">Mobile:</span>{" "}
                          {selectedOrder?.customer?.contactMobile ||
                            "Not provided"}
                        </div>
                        <div>
                          <span className="font-medium">Email:</span>{" "}
                          {selectedOrder?.customer?.email || "Not provided"}
                        </div>
                        {selectedOrder?.customer?.contactEmail &&
                          selectedOrder?.customer?.contactEmail !==
                            selectedOrder?.customer?.email && (
                            <div>
                              <span className="font-medium">Alt Email:</span>{" "}
                              {selectedOrder?.customer?.contactEmail}
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Delivery Address */}
                    <div className="bg-white p-3 rounded border-l-4 border-orange-500">
                      <div className="font-bold text-orange-700 mb-2 text-sm">
                        Delivery Address
                      </div>
                      <div className="text-sm space-y-1">
                        <div>
                          {selectedOrder?.customer?.shipAddressLine ||
                            "Address not provided"}
                        </div>
                        <div>
                          {[
                            selectedOrder?.customer?.shipDistrict,
                            selectedOrder?.customer?.shipState,
                            selectedOrder?.customer?.shipPinCode,
                          ]
                            .filter(Boolean)
                            .join(", ") || "Location details not available"}
                        </div>
                        <div>
                          {selectedOrder?.customer?.shipCountry ||
                            "Country not specified"}
                        </div>
                        {selectedOrder?.customer?.shipGpsLocation && (
                          <div className="text-xs text-gray-500 mt-1">
                            <span className="font-medium">GPS:</span>{" "}
                            {selectedOrder.customer.shipGpsLocation}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Instructions */}
                <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                  <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    Delivery Instructions & Special Notes
                  </h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Customer's Delivery Preference
                      </label>
                      <input
                        type="text"
                        value={
                          selectedOrder?.customer?.preferredDeliveryMethod ||
                          "Standard"
                        }
                        disabled
                        className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Customer's Special Instructions
                      </label>
                      <input
                        type="text"
                        value={
                          selectedOrder?.customer?.specialInstructions || "None"
                        }
                        disabled
                        className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Additional Delivery Instructions
                    </label>
                    <textarea
                      value={deliveryNotes}
                      onChange={(e) => setDeliveryNotes(e.target.value)}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      placeholder="Add any specific delivery instructions, contact details, or special handling requirements..."
                    />
                  </div>
                </div>

                {/* Delivery Items */}
                <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
                  <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                    Items for Delivery
                  </h4>
                  {selectedOrder?.poNumber && (
                    <div className="mb-3 text-sm text-indigo-700 font-medium">
                      Purchase Order:{" "}
                      <span className="font-semibold">
                        {selectedOrder.poNumber}
                      </span>
                    </div>
                  )}
                  <div className="overflow-x-auto bg-white rounded-lg border shadow-sm">
                    <table className="w-full min-w-[800px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Product Name & Description
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Barcode
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Ordered Qty
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Previously Delivered
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Available Stock
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Delivering Now
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Unit
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {deliveryItems.map((item, index) => {
                          const inventoryInfo = getInventoryInfo(
                            item.productId,
                            item.productSkuId
                          );
                          const barcode = getBarcode(item);

                          return (
                            <tr
                              key={`delivery-item-${index}`}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-4 py-3 text-sm">
                                <div className="font-medium text-gray-900">
                                  {item.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {item.description ||
                                    "No description available"}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center font-mono">
                                <div className="font-medium">{barcode}</div>
                                {loadingInventory ? (
                                  <div className="text-xs text-gray-500">
                                    Loading...
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {getSelectedBatchInfo(
                                      item.productId,
                                      item.productSkuId
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {item.quantity}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {item.deliveredQuantity || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                {loadingInventory ? (
                                  <div className="text-xs text-gray-500">
                                    Loading...
                                  </div>
                                ) : (
                                  <div className="space-y-1">
                                    <div
                                      className={`font-bold ${
                                        inventoryInfo.available <
                                        item.pendingQuantity
                                          ? "text-red-600"
                                          : "text-blue-600"
                                      }`}
                                    >
                                      {inventoryInfo.available}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Total: {inventoryInfo.total} | Reserved:{" "}
                                      {inventoryInfo.reserved}
                                    </div>
                                    {inventoryInfo.available <
                                      item.pendingQuantity && (
                                      <div className="text-xs text-red-600 font-medium">
                                        ⚠️ Insufficient stock
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="space-y-1">
                                  <input
                                    type="number"
                                    min="0"
                                    max={item.maxDelivery}
                                    value={item.deliverNow}
                                    onChange={(e) =>
                                      handleDeliveryItemChange(
                                        index,
                                        "deliverNow",
                                        e.target.value
                                      )
                                    }
                                    className={`w-20 px-2 py-2 border rounded-md text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium ${
                                      item.deliverNow > item.maxDelivery
                                        ? "border-red-500 bg-red-50"
                                        : "border-gray-300"
                                    }`}
                                    placeholder="0"
                                  />
                                  <div className="text-xs text-gray-500">
                                    Max: {item.maxDelivery}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                {item.unit || "PCS"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-indigo-50">
                        <tr>
                          <td
                            colSpan="5"
                            className="px-4 py-3 text-sm font-medium text-gray-700"
                          >
                            Total Items for Delivery:{" "}
                            <span className="text-blue-600 font-bold">
                              {deliveryItems.reduce(
                                (sum, item) => sum + (item.deliverNow || 0),
                                0
                              )}
                            </span>{" "}
                            Items
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-700 text-center">
                            {deliveryItems.reduce(
                              (sum, item) => sum + (item.deliverNow || 0),
                              0
                            )}
                          </td>
                          <td className="px-4 py-3"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Customer Acknowledgment Section */}
                <div className="bg-rose-50 p-4 rounded-lg border-l-4 border-rose-500">
                  <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-rose-500 rounded-full mr-2"></span>
                    Customer Acknowledgment & Receipt Confirmation
                  </h4>
                  <div className="bg-white p-4 rounded-lg border border-rose-200">
                    <div className="prose prose-sm max-w-none">
                      <p className="text-sm text-gray-700 mb-4">
                        <strong>DELIVERY ACKNOWLEDGMENT STATEMENT:</strong>
                      </p>
                      <div className="bg-gray-50 p-3 rounded text-sm text-gray-700 mb-4">
                        I hereby acknowledge that I have received the
                        above-mentioned goods in good condition and in the
                        quantities specified. The delivered items have been
                        inspected and found to be in accordance with the order
                        specifications. Any discrepancies or damages should be
                        reported within 24 hours of delivery.
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Customer Name (Print)
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                            placeholder="Print customer name clearly"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Customer Signature
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                            placeholder="Digital signature or initials"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Date of Receipt
                          </label>
                          <input
                            type="date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                            defaultValue={
                              new Date().toISOString().split("T")[0]
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Time of Receipt
                          </label>
                          <input
                            type="time"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                            defaultValue={new Date().toTimeString().slice(0, 5)}
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          Additional Notes/Comments
                        </label>
                        <textarea
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                          placeholder="Any additional comments about the delivery condition or special notes..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Sticky Footer with Action Buttons */}
            <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 rounded-b-lg flex-shrink-0">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="text-sm text-gray-600 text-center sm:text-left">
                  <div className="space-y-2">
                    <div className="space-x-4">
                      <span>
                        <span className="font-medium">Items to Deliver:</span>{" "}
                        {
                          deliveryItems.filter((item) => item.deliverNow > 0)
                            .length
                        }{" "}
                        products
                      </span>
                      <span>
                        <span className="font-medium">Total Quantity:</span>{" "}
                        {deliveryItems.reduce(
                          (sum, item) => sum + (parseInt(item.deliverNow) || 0),
                          0
                        )}{" "}
                        units
                      </span>
                    </div>
                    {deliveryItems.some(
                      (item) => item.maxDelivery < item.pendingQuantity
                    ) && (
                      <div className="text-xs text-red-600 font-medium">
                        ⚠️ Some items have insufficient stock. Maximum available
                        quantities will be used.
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-3 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setShowDOModal(false)}
                    className="flex-1 sm:flex-none px-4 sm:px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDO}
                    disabled={creatingDO}
                    className="flex-1 sm:flex-none px-4 sm:px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {creatingDO ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Creating...
                      </>
                    ) : (
                      "Create DO & Generate PDF"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Creation Prompt Modal */}
      <Modal
        isOpen={showInvoicePrompt}
        onClose={() => {
          setShowInvoicePrompt(false);
          setLastCreatedDO(null);
          setSelectedOrder(null);
        }}
        title="Delivery Order Created Successfully"
        size="md"
      >
        <div className="text-center py-4">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Delivery Order Created!
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            The delivery order has been created and the PDF has been generated.
            Would you like to create an invoice for this delivery?
          </p>

          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700">Order:</span>
              <span className="text-gray-900">{selectedOrder?.id}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="font-medium text-gray-700">Customer:</span>
              <span className="text-gray-900">
                {selectedOrder?.customer?.businessName}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="font-medium text-gray-700">
                Items Delivered:
              </span>
              <span className="text-gray-900">
                {deliveryItems.filter((item) => item.deliverNow > 0).length}{" "}
                products
              </span>
            </div>
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setShowInvoicePrompt(false);
                setLastCreatedDO(null);
                setSelectedOrder(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Later
            </button>
            <button
              onClick={() => {
                setShowInvoicePrompt(false);
                handleCreateInvoice(selectedOrder);
                setLastCreatedDO(null);
              }}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Create Invoice Now
            </button>
          </div>
        </div>
      </Modal>

      <CommercialInvoiceForm
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        selectedOrder={selectedOrder}
        onConfirmInvoice={handleConfirmInvoice}
        creatingInvoice={creatingInvoice}
        companyInfo={companyInfo} // Pass company information from company setup
      />

      {/* Order Details Modal using PurchaseOrderForm */}
      {showOrderDetails && selectedOrder && (
        <PurchaseOrderForm
          isOpen={showOrderDetails}
          onClose={() => setShowOrderDetails(false)}
          cartItems={[]} // Empty cart for view-only mode
          onSubmit={() => {}} // No submit for view-only
          onConfirmPO={() => {}} // No confirm for view-only
          onSaveDraft={() => {}} // No save for view-only
          customerInfo={{
            id: selectedOrder.customer?.id,
            name: selectedOrder.customer?.name,
            companyName: selectedOrder.customer?.businessName,
            email: selectedOrder.customer?.email,
            phone: selectedOrder.customer?.contactMobile,
            contactName: selectedOrder.customer?.contactName,
            contactRole: selectedOrder.customer?.contactRole,
            contactEmail: selectedOrder.customer?.contactEmail,
            contactMobile: selectedOrder.customer?.contactMobile,
            contactAltMobile: selectedOrder.customer?.contactAltMobile,
            // Billing address details
            billAddress: selectedOrder.customer?.billAddressLine || "",
            billCity: selectedOrder.customer?.billDistrict || "",
            billState: selectedOrder.customer?.billState || "",
            billZipCode: selectedOrder.customer?.billPinCode || "",
            billCountry: selectedOrder.customer?.billCountry || "India",
            billGpsLocation: selectedOrder.customer?.billGpsLocation,
            // Shipping address details
            shipAddress: selectedOrder.customer?.shipAddressLine || "",
            shipCity: selectedOrder.customer?.shipDistrict || "",
            shipState: selectedOrder.customer?.shipState || "",
            shipZipCode: selectedOrder.customer?.shipPinCode || "",
            shipCountry: selectedOrder.customer?.shipCountry || "India",
            shipGpsLocation: selectedOrder.customer?.shipGpsLocation,
            // Business details
            businessCategory: selectedOrder.customer?.businessCategory,
            gstNumber: selectedOrder.customer?.gstNumber,
            panNumber: selectedOrder.customer?.panNumber,
            yearsInBusiness: selectedOrder.customer?.yearsInBusiness,
            // Legacy fields for backward compatibility
            address: selectedOrder.customer?.billAddressLine || "",
            city: selectedOrder.customer?.billDistrict || "",
            state: selectedOrder.customer?.billState || "",
            zipCode: selectedOrder.customer?.billPinCode || "",
            country: selectedOrder.customer?.billCountry || "India",
          }}
          companyInfo={companyInfo} // Pass company information from company setup
          draftOrder={selectedOrder} // Pass the order as draft to populate form
          status={selectedOrder.status}
          viewOnly={true} // Add view-only mode
        />
      )}

      {/* Old View Delivery Order Modal - Replaced with Professional Modal */}
      {false && showViewDOModal && selectedDeliveryOrder && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl my-4 sm:my-8 flex flex-col max-h-[95vh] sm:max-h-[90vh]">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold leading-tight flex items-center gap-3">
                    VIEW DELIVERY ORDER
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                        selectedDeliveryOrder.deliveryStatus === "delivered"
                          ? "bg-green-500 text-white"
                          : selectedDeliveryOrder.deliveryStatus ===
                            "dispatched"
                          ? "bg-blue-500 text-white"
                          : selectedDeliveryOrder.deliveryStatus ===
                            "processing"
                          ? "bg-yellow-400 text-yellow-900"
                          : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      {selectedDeliveryOrder.deliveryStatus?.toUpperCase()}
                    </span>
                  </h2>
                  <p className="text-green-100 text-sm mt-1">
                    DO Number: {selectedDeliveryOrder.deliveryNumber}
                  </p>
                </div>
                <button
                  onClick={() => setShowViewDOModal(false)}
                  className="text-green-100 hover:text-white text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Order Reference Section */}
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-blue-600">📋</span> Order Reference
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600 font-medium">Order ID</div>
                      <div className="text-gray-900">
                        {selectedOrder.poNumber ||
                          selectedOrder.orderNumber ||
                          selectedOrder.id}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 font-medium">
                        Order Date
                      </div>
                      <div className="text-gray-900">
                        {format(
                          new Date(selectedOrder.createdAt),
                          "dd MMM yyyy"
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 font-medium">PO Number</div>
                      <div className="text-gray-900">
                        {selectedOrder.poNumber || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 font-medium">
                        Order Status
                      </div>
                      <div className="text-gray-900">
                        {selectedOrder.status}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Information Section */}
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-green-600">🏢</span> Customer
                    Information
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600 font-medium">
                        Business Name
                      </div>
                      <div className="text-gray-900">
                        {selectedOrder.customer?.businessName || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 font-medium">
                        Contact Person
                      </div>
                      <div className="text-gray-900">
                        {selectedDeliveryOrder.contactPerson ||
                          selectedOrder.customer?.contactName ||
                          "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 font-medium">
                        Contact Phone
                      </div>
                      <div className="text-gray-900">
                        {selectedDeliveryOrder.contactPhone ||
                          selectedOrder.customer?.contactMobile ||
                          "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Schedule Section */}
                <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-yellow-600">🚚</span> Delivery
                    Schedule
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600 font-medium">
                        Expected Date
                      </div>
                      <div className="text-gray-900">
                        {selectedDeliveryOrder.expectedDeliveryDate
                          ? format(
                              new Date(
                                selectedDeliveryOrder.expectedDeliveryDate
                              ),
                              "dd MMM yyyy"
                            )
                          : "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 font-medium">
                        Actual Date
                      </div>
                      <div className="text-gray-900">
                        {selectedDeliveryOrder.actualDeliveryDate
                          ? format(
                              new Date(
                                selectedDeliveryOrder.actualDeliveryDate
                              ),
                              "dd MMM yyyy"
                            )
                          : "Pending"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 font-medium">
                        Delivery Method
                      </div>
                      <div className="text-gray-900 capitalize">
                        {selectedDeliveryOrder.deliveryMethod || "Standard"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600 font-medium">Status</div>
                      <div className="text-gray-900 capitalize">
                        {selectedDeliveryOrder.deliveryStatus}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ship-To Address Section */}
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-purple-600">📍</span> Ship-To Address
                  </h3>
                  <div className="text-sm">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <div className="font-medium text-gray-900 mb-2">
                          {selectedOrder.customer?.businessName || "N/A"}
                        </div>
                        <div className="text-gray-700 space-y-1">
                          <div>
                            {selectedOrder.customer?.shipAddressLine || "N/A"}
                          </div>
                          <div>
                            {selectedOrder.customer?.shipDistrict &&
                              `${selectedOrder.customer.shipDistrict}, `}
                            {selectedOrder.customer?.shipState} -{" "}
                            {selectedOrder.customer?.shipPinCode}
                          </div>
                          <div>
                            {selectedOrder.customer?.shipCountry || "India"}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-600 space-y-1">
                          <div>
                            <strong>Contact:</strong>{" "}
                            {selectedOrder.customer?.contactName || "N/A"}
                          </div>
                          <div>
                            <strong>Phone:</strong>{" "}
                            {selectedOrder.customer?.contactMobile || "N/A"}
                          </div>
                          <div>
                            <strong>Email:</strong>{" "}
                            {selectedOrder.customer?.email || "N/A"}
                          </div>
                          {selectedOrder.customer?.shipGpsLocation && (
                            <div>
                              <strong>GPS:</strong>{" "}
                              {selectedOrder.customer.shipGpsLocation}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Instructions Section */}
                {selectedDeliveryOrder.deliveryInstructions && (
                  <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <span className="text-orange-600">📝</span> Delivery
                      Instructions
                    </h3>
                    <div className="text-sm text-gray-700">
                      {selectedDeliveryOrder.deliveryInstructions}
                    </div>
                  </div>
                )}

                {/* Delivery Items Section */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-indigo-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <span className="text-indigo-600">📦</span> Delivery Items
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Product Name & Description
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            SKU/Part No.
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Delivered Qty
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Unit
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Condition
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedDeliveryOrder.items?.map((item, index) => (
                          <tr
                            key={`view-do-item-${index}`}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-4 py-3 text-sm">
                              <div className="font-medium text-gray-900">
                                {item.orderItem?.product?.name ||
                                  item.productName ||
                                  "N/A"}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {item.orderItem?.product?.description ||
                                  item.description ||
                                  "No description available"}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 text-center font-mono">
                              {item.orderItem?.product?.sku ||
                                item.sku ||
                                item.productId ||
                                "N/A"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {item.quantity}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                              {item.unit || "PCS"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Good
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 font-medium">
                          <td
                            colSpan="2"
                            className="px-4 py-3 text-sm text-gray-700 text-right"
                          >
                            <strong>Total Items Delivered:</strong>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-green-200 text-green-800">
                              {selectedDeliveryOrder.items?.reduce(
                                (sum, item) => sum + (item.quantity || 0),
                                0
                              ) || 0}
                            </span>
                          </td>
                          <td
                            colSpan="2"
                            className="px-4 py-3 text-sm text-gray-700 text-center"
                          >
                            Items
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Customer Acknowledgment Section (if delivered) */}
                {selectedDeliveryOrder.deliveryStatus === "delivered" && (
                  <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <span className="text-green-600">✅</span> Delivery
                      Acknowledgment
                    </h3>
                    <div className="text-sm text-gray-700 space-y-2">
                      <p className="font-medium">
                        I acknowledge receipt of the above items in good
                        condition and confirm that the delivery is complete as
                        per the order requirements.
                      </p>
                      <div className="flex justify-between items-center mt-4">
                        <div>
                          <div className="text-gray-600">
                            Customer Signature
                          </div>
                          <div className="border-b border-gray-300 w-48 h-8"></div>
                        </div>
                        <div>
                          <div className="text-gray-600">Date & Time</div>
                          <div className="text-gray-900">
                            {selectedDeliveryOrder.actualDeliveryDate
                              ? format(
                                  new Date(
                                    selectedDeliveryOrder.actualDeliveryDate
                                  ),
                                  "dd MMM yyyy, HH:mm"
                                )
                              : format(new Date(), "dd MMM yyyy, HH:mm")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 rounded-b-lg flex-shrink-0">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>DO Number:</strong>{" "}
                  {selectedDeliveryOrder.deliveryNumber} |
                  <strong className="ml-2">Status:</strong>{" "}
                  {selectedDeliveryOrder.deliveryStatus} |
                  <strong className="ml-2">Total Items:</strong>{" "}
                  {selectedDeliveryOrder.items?.reduce(
                    (sum, item) => sum + (item.quantity || 0),
                    0
                  ) || 0}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowViewDOModal(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      // Generate and download PDF of the delivery order
                      const deliveryOrderData = {
                        deliveryNumber: selectedDeliveryOrder.deliveryNumber,
                        deliveryStatus: selectedDeliveryOrder.deliveryStatus,
                        expectedDeliveryDate:
                          selectedDeliveryOrder.expectedDeliveryDate,
                        actualDeliveryDate:
                          selectedDeliveryOrder.actualDeliveryDate,
                        deliveryMethod: selectedDeliveryOrder.deliveryMethod,
                        deliveryAddress: {
                          businessName: selectedOrder.customer?.businessName,
                          addressLine: selectedOrder.customer?.shipAddressLine,
                          district: selectedOrder.customer?.shipDistrict,
                          state: selectedOrder.customer?.shipState,
                          pinCode: selectedOrder.customer?.shipPinCode,
                          country: selectedOrder.customer?.shipCountry,
                        },
                        contactPerson:
                          selectedDeliveryOrder.contactPerson ||
                          selectedOrder.customer?.contactName,
                        contactPhone:
                          selectedDeliveryOrder.contactPhone ||
                          selectedOrder.customer?.contactMobile,
                        deliveryInstructions:
                          selectedDeliveryOrder.deliveryInstructions,
                        items:
                          selectedDeliveryOrder.items?.map((item) => ({
                            productName:
                              item.orderItem?.product?.name ||
                              item.productName ||
                              "N/A",
                            description:
                              item.orderItem?.product?.description ||
                              item.description ||
                              "No description available",
                            sku:
                              item.orderItem?.product?.sku ||
                              item.sku ||
                              item.productId ||
                              "N/A",
                            quantity: item.quantity,
                            unit: item.unit || "PCS",
                          })) || [],
                      };
                      generateProfessionalDOPDF(
                        selectedDeliveryOrder,
                        selectedOrder
                      );
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Professional Delivery Order Modal */}
      {selectedDeliveryOrder && selectedOrder && (
        <ProfessionalDeliveryOrderModal
          deliveryOrder={selectedDeliveryOrder}
          order={selectedOrder}
          isOpen={showViewDOModal}
          onClose={() => setShowViewDOModal(false)}
          onDownload={generateProfessionalDOPDF}
          companyInfo={companyInfo} // Pass company information from company setup
        />
      )}

      {/* Insufficient Stock Warning Modal */}
      <Modal
        isOpen={showInsufficientStockModal}
        onClose={handleCancelInsufficientStock}
        title="⚠️ Insufficient Stock Warning"
      >
        <div className="space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-amber-800 dark:text-amber-200 text-sm font-medium mb-3">
              Some items have insufficient stock. The delivery will be limited
              to available stock.
            </p>

            {insufficientStockItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Items with insufficient stock:
                </p>
                <ul className="space-y-2">
                  {insufficientStockItems.map((item, index) => (
                    <li
                      key={index}
                      className="bg-white dark:bg-slate-800 rounded p-3 border border-gray-200 dark:border-slate-700"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            SKU: {item.sku || "N/A"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Requested:{" "}
                            <span className="font-semibold">
                              {item.pendingQuantity}
                            </span>
                          </p>
                          <p className="text-sm text-amber-600 dark:text-amber-400">
                            Available:{" "}
                            <span className="font-semibold">
                              {item.maxDelivery}
                            </span>
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              <strong>Note:</strong> The system will automatically adjust
              quantities to match available stock. You can create additional
              delivery orders later when stock is replenished.
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            onClick={handleCancelInsufficientStock}
            style={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmInsufficientStock}
            style={{ flex: 1 }}
            disabled={creatingDO}
          >
            {creatingDO ? "Creating..." : "Continue with Available Stock"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
